// Dummy clinical data for Margaret Thompson (CRN-PAT-00482)
// Intentionally consistent with her known conditions AND what RAAPID suspects

export const CERNER_VITALS = [
  { date: '04/21/2026', bp: '148/92', hr: '74 reg', rr: '16', temp: '98.2°F', spo2: '95%', wt: '248 lbs', bmi: '42.6', pain: '1/10' },
  { date: '01/15/2026', bp: '152/88', hr: '78 reg', rr: '16', temp: '98.6°F', spo2: '96%', wt: '245 lbs', bmi: '42.1', pain: '0/10' },
  { date: '10/22/2025', bp: '144/90', hr: '72 reg', rr: '15', temp: '97.8°F', spo2: '94%', wt: '242 lbs', bmi: '41.5', pain: '2/10' },
];

export const CERNER_CONDITIONS = [
  { icd: 'E11.65', display: 'Type 2 diabetes mellitus with hyperglycemia', status: 'Active', onset: '2015-03-10' },
  { icd: 'E78.2',  display: 'Mixed hyperlipidemia',                          status: 'Active', onset: '2014-07-22' },
  { icd: 'N18.31', display: 'Chronic kidney disease, stage 3a',             status: 'Active', onset: '2019-11-05' },
  { icd: 'G47.33', display: 'Obstructive sleep apnea (adult)',              status: 'Active', onset: '2020-02-14' },
  { icd: 'E66.01', display: 'Morbid obesity due to excess calories',        status: 'Active', onset: '2012-06-30' },
  // NOTE: I13.10 (Hypertensive heart+CKD) and F32.1 (MDD) are NOT here — RAAPID flags them
];

export const CERNER_MEDICATIONS = [
  { name: 'Metformin 1000 mg tablet',      sig: 'Take 1 tablet by mouth twice daily with meals', refills: 3, date: '2026-01-15' },
  { name: 'Jardiance 25 mg tablet',        sig: 'Take 1 tablet by mouth once daily',             refills: 3, date: '2025-06-10' },
  { name: 'Ozempic 0.25 mg injection',     sig: 'Inject subcutaneously once weekly, titrate to 1 mg', refills: 2, date: '2026-03-28' },
  { name: 'Atorvastatin 40 mg tablet',     sig: 'Take 1 tablet by mouth at bedtime',             refills: 5, date: '2025-10-22' },
  { name: 'Lisinopril 40 mg tablet',       sig: 'Take 1 tablet by mouth once daily',             refills: 5, date: '2025-10-22' },
  { name: 'Amlodipine 10 mg tablet',       sig: 'Take 1 tablet by mouth once daily',             refills: 5, date: '2025-10-22' },
  { name: 'Aspirin 81 mg tablet',          sig: 'Take 1 tablet by mouth once daily',             refills: 11, date: '2024-04-16' },
  { name: 'Gabapentin 300 mg capsule',     sig: 'Take 1 capsule by mouth three times daily',     refills: 3, date: '2025-08-14' },
  { name: 'CPAP — 12 cmH₂O',              sig: 'Use nightly for obstructive sleep apnea',       refills: 0, date: '2024-09-10' },
];

export const CERNER_LABS = [
  { name: 'Hemoglobin A1c',    value: '8.2 %',        ref: '< 5.7%',   flag: 'H', date: '2026-01-15', note: 'Improved from 8.9%' },
  { name: 'Serum Creatinine',  value: '1.4 mg/dL',    ref: '0.5–1.1',  flag: 'H', date: '2026-04-10', note: '' },
  { name: 'eGFR',              value: '52 mL/min',     ref: '> 60',     flag: 'L', date: '2026-04-10', note: 'CKD Stage 3a' },
  { name: 'BUN',               value: '22 mg/dL',      ref: '7–25',     flag: '',  date: '2026-04-10', note: '' },
  { name: 'Sodium',            value: '138 mEq/L',     ref: '136–145',  flag: '',  date: '2026-04-10', note: '' },
  { name: 'Potassium',         value: '4.1 mEq/L',     ref: '3.5–5.0',  flag: '',  date: '2026-04-10', note: '' },
  { name: 'LDL Cholesterol',   value: '112 mg/dL',     ref: '< 100',    flag: 'H', date: '2025-10-22', note: '' },
  { name: 'HDL Cholesterol',   value: '42 mg/dL',      ref: '> 60',     flag: 'L', date: '2025-10-22', note: '' },
  { name: 'Total Cholesterol', value: '198 mg/dL',     ref: '< 200',    flag: '',  date: '2025-10-22', note: '' },
  { name: 'Urine Albumin/Cr',  value: '48 mg/g',       ref: '< 30',     flag: 'H', date: '2025-10-22', note: 'Microalbuminuria' },
];

export const CERNER_NOTES = [
  {
    date: '04/21/2026',
    type: 'Annual Wellness Visit',
    provider: 'Dr. Sarah Chen, MD',
    excerpt: `SUBJECTIVE:
Mrs. Thompson is a 77-year-old female presenting for her Medicare Annual Wellness Visit. She reports generally feeling "okay" but endorses persistent fatigue over the past 3–4 months, decreased motivation, poor sleep quality despite CPAP use, and reduced interest in her usual activities including gardening and church group. She denies suicidal ideation or self-harm. She reports occasional bilateral lower extremity swelling, worse at end of day, improved with elevation. She states her blood sugars at home range 140–210 fasting. She reports good adherence to metformin and Jardiance. Ozempic was started 3 weeks ago — tolerating well, mild nausea first week that has resolved. She denies chest pain, dyspnea on exertion, orthopnea, or PND. Diet remains high in processed carbohydrates despite counseling. CPAP compliance per device download: 5.2 hrs/night average, AHI 3.2 (controlled).

OBJECTIVE:
Vitals: BP 148/92 mmHg (seated, left arm, manual cuff), HR 74 bpm regular, RR 16, Temp 98.2°F, SpO2 95% on RA, Weight 248 lbs, BMI 42.6 kg/m².
General: Alert, oriented x3, well-appearing, obese female in no acute distress.
HEENT: Normocephalic, atraumatic. Oropharynx clear. Mallampati Class IV. Neck supple, no thyromegaly, no JVD.
Cardiovascular: Regular rate and rhythm. S1/S2 normal. No S3 or S4 appreciated. No murmurs, rubs, or gallops. PMI non-displaced. Bilateral lower extremity trace pitting edema to mid-shin.
Pulmonary: Clear to auscultation bilaterally. No wheezes, rhonchi, or crackles. Good air movement.
Abdomen: Soft, non-tender, non-distended. Normoactive bowel sounds. No hepatosplenomegaly.
Extremities: Trace bilateral pedal edema as noted. Pedal pulses 2+ bilaterally. No skin breakdown. Monofilament testing: diminished sensation bilateral plantar surfaces (3/10 sites).
Neuropsych: Affect flat. Speech normal rate and rhythm. PHQ-2 screening positive (score 4/6) — full PHQ-9 ordered.

Labs reviewed (drawn 04/10/2026):
- BMP: Creatinine 1.4 mg/dL (H), BUN 22, Na 138, K 4.1, Cl 102, CO2 24, Glucose 186 (H)
- eGFR: 52 mL/min/1.73m² (CKD Stage 3a, stable from 55 in January)
- HbA1c (01/15/2026): 8.2% (improved from 8.9%)
- Lipid panel (10/2025): LDL 112 (H), HDL 42 (L), TG 188, Total 198
- Urine Albumin/Creatinine Ratio (10/2025): 48 mg/g (microalbuminuria)

Prior imaging reviewed:
- Echocardiogram (03/2025): LVEF 55%, mild concentric LVH, Grade I diastolic dysfunction, no valvular abnormalities, LA mildly dilated at 4.2 cm.

ASSESSMENT:
1. Type 2 diabetes mellitus with hyperglycemia (E11.65) — A1c improved 8.9% → 8.2% on metformin + Jardiance. Ozempic recently initiated. Still above ADA target of <7% for this age cohort. Diabetic peripheral neuropathy on exam — not yet formally coded.
2. Morbid obesity, BMI 42.6 (E66.01) — Weight stable at 248 lbs. Ozempic may assist with weight management. Patient continues to struggle with dietary modifications.
3. Hypertension, poorly controlled — BP 148/92 today, trending >140/90 across last 3 visits despite dual antihypertensive therapy (lisinopril 40mg + amlodipine 10mg). Given concentric LVH on echocardiogram and concurrent CKD Stage 3, clinical picture is consistent with hypertensive heart disease with CKD. Consider reclassifying to I13.10 at cardiology evaluation.
4. Chronic kidney disease, Stage 3a (N18.3) — eGFR stable 52. Microalbuminuria persists. ACE inhibitor maximized. Nephrology following.
5. Obstructive sleep apnea (G47.33) — Controlled on CPAP. Adequate compliance at 5.2 hrs/night.
6. Dyslipidemia (E78.5) — LDL 112, above goal of <70 given ASCVD risk factors. Statin intensification warranted.
7. Suspected major depressive disorder — PHQ-2 positive today. Patient endorses anhedonia, fatigue, poor sleep, decreased motivation over past 3–4 months. Flat affect on exam. Full PHQ-9 pending for formal scoring. If confirmed, would code F32.1 (moderate depressive episode).

PLAN:
1. Diabetes: Continue metformin 1000mg BID, Jardiance 25mg daily. Titrate Ozempic to 0.5mg weekly at next injection, goal 1mg. Recheck A1c in 3 months. Reinforce carbohydrate counting and dietary counseling. Diabetic eye exam referral — overdue.
2. Obesity: Continue Ozempic titration. Nutritionist referral placed. Discussed realistic weight loss goals of 5–10% body weight over 12 months.
3. Hypertension/suspected hypertensive heart disease: Refer to cardiology for evaluation — repeat echocardiogram and assessment for hypertensive heart disease with CKD. If confirmed, code will be updated to I13.10. In interim, continue lisinopril 40mg + amlodipine 10mg. Consider adding chlorthalidone 12.5mg if BP remains uncontrolled at follow-up.
4. CKD: Continue nephrology co-management. Recheck BMP + UACR in 3 months. Avoid nephrotoxic agents. Continue ACE inhibitor for renal protection.
5. OSA: Continue CPAP. Compliance adequate. Annual sleep study due — will order.
6. Dyslipidemia: Increase atorvastatin from 40mg to 80mg daily given elevated LDL and high ASCVD risk. Recheck lipid panel in 6 weeks.
7. Depression screening: Complete PHQ-9 today. If score ≥10, initiate SSRI (sertraline 50mg daily preferred given CKD). Consider psychiatry referral if severe.
8. Preventive care: Influenza vaccine given today. Pneumovax 23 — verify status. Colonoscopy — last 2018, due for repeat.

Return to clinic in 6–8 weeks for BP recheck and depression follow-up. Sooner PRN.`,
  },
  {
    date: '01/15/2026',
    type: 'Diabetes Follow-up',
    provider: 'Dr. Sarah Chen, MD',
    excerpt: `SUBJECTIVE:
Mrs. Thompson presents for routine diabetes follow-up. She reports improved adherence to metformin since last visit. Home glucose logs show fasting 150–200 range, post-prandial 180–260. She denies hypoglycemic episodes. She reports numbness and tingling in bilateral feet, "worse at night," partially relieved by gabapentin. She continues CPAP nightly. She reports mild fatigue but attributes it to poor sleep. She denies polyuria, polydipsia, or blurred vision changes. Diet remains a challenge — patient lives alone and relies on convenience foods.

OBJECTIVE:
Vitals: BP 152/88 mmHg, HR 78 bpm regular, RR 16, Temp 98.6°F, SpO2 96% on RA, Weight 245 lbs, BMI 42.1 kg/m².
General: Obese female, alert, oriented, cooperative.
Cardiovascular: RRR, no murmurs. Trace bilateral pedal edema.
Extremities: Feet warm, pulses palpable. Monofilament exam: diminished sensation bilateral plantar 4/10 sites.
Skin: No foot ulcers or callus breakdown.

Labs today:
- HbA1c: 8.2% (previously 8.9% on 07/2025) — improved
- Fasting glucose: 178 mg/dL (H)
- eGFR: 55 mL/min — CKD Stage 3a, stable
- Creatinine: 1.3 mg/dL

ASSESSMENT:
1. Type 2 diabetes mellitus with hyperglycemia (E11.65) — A1c improving on current regimen but still above goal. Fasting and post-prandial glucoses remain elevated. Benefits of GLP-1 receptor agonist discussed given obesity and A1c burden.
2. Diabetic peripheral neuropathy — bilateral foot numbness with diminished monofilament. Gabapentin providing partial relief. Clinical diagnosis, not yet formally coded.
3. Morbid obesity, BMI 42.1 (E66.01) — Stable weight. Discussed GLP-1 RA for dual benefit (glycemic control + weight loss).
4. CKD Stage 3a (N18.3) — eGFR stable at 55. Continue renal-protective measures.
5. Hypertension — BP 152/88, above target. Currently on lisinopril 40mg + amlodipine 10mg. May need third agent.

PLAN:
1. Start Ozempic (semaglutide) 0.25mg SQ weekly — titrate per protocol. Prior authorization submitted. Expected dual benefit for glycemia and weight. Continue metformin 1000mg BID and Jardiance 25mg daily.
2. Continue gabapentin 300mg TID for neuropathy. If inadequate relief, consider duloxetine (also may help mood).
3. Continue current antihypertensives. Recheck BP at next visit — if still >140/90, will add third agent.
4. Dietary counseling reinforced. Provided handout on diabetic meal planning. Discussed Meals on Wheels program.
5. Diabetic foot care education reviewed. Inspect feet daily. Follow up with podiatry annually.
6. Recheck A1c, BMP in 3 months.
7. Return to clinic 3 months.`,
  },
  {
    date: '10/22/2025',
    type: 'Routine Follow-up',
    provider: 'Dr. Sarah Chen, MD',
    excerpt: `SUBJECTIVE:
Mrs. Thompson presents for routine follow-up of her chronic conditions. She reports feeling "about the same." She denies any new complaints. She continues CPAP nightly — reports sleeping better with it. She endorses some bilateral foot tingling at night. She denies chest pain, shortness of breath, or swelling. She has been walking her dog daily for about 15 minutes. Diet remains unchanged. She reports taking all medications as prescribed.

OBJECTIVE:
Vitals: BP 144/90 mmHg, HR 72 bpm regular, RR 15, Temp 97.8°F, SpO2 94% on RA (retested 96% with deep breathing), Weight 242 lbs, BMI 41.5 kg/m².
General: Obese female, alert, in no acute distress.
Cardiovascular: RRR, S1/S2 normal, no murmurs or gallops. No peripheral edema today.
Pulmonary: Clear bilaterally.
Extremities: Pedal pulses intact. No skin breakdown.

Labs ordered today: BMP, lipid panel, urine albumin/creatinine ratio.

ASSESSMENT:
1. Type 2 diabetes mellitus with hyperglycemia (E11.65) — A1c due, last checked 07/2025 at 8.9%. Will reassess glycemic control.
2. Hypertension — BP 144/90, mildly above target on dual therapy. Monitoring.
3. Obstructive sleep apnea (G47.33) — Well-controlled on CPAP.
4. Morbid obesity (E66.01) — Weight 242 lbs, stable. Continued lifestyle counseling.
5. Dyslipidemia (E78.5) — Lipid panel ordered for reassessment. On atorvastatin 40mg.
6. CKD Stage 3 (N18.3) — Will recheck eGFR with today's labs.

PLAN:
1. Continue current medications unchanged.
2. Labs ordered: BMP, lipid panel, UACR. Will contact patient with results.
3. Continue CPAP. Compliance appears adequate per patient report.
4. Reinforce low-sodium, diabetic diet. Encouraged increased physical activity.
5. Schedule A1c check at next visit.
6. Return to clinic in 3 months. Annual Wellness Visit due in April 2026.`,
  },
  {
    date: '07/08/2025',
    type: 'Nephrology Consult',
    provider: 'Dr. James Park, MD — Nephrology',
    excerpt: `SUBJECTIVE:
Mrs. Thompson is a 76-year-old female referred by Dr. Sarah Chen for ongoing management of chronic kidney disease. Patient reports no new urinary symptoms — denies hematuria, dysuria, frequency changes, or flank pain. She states she tries to drink adequate water but admits to not following the low-protein diet recommendations consistently. She reports good medication adherence. She endorses bilateral foot numbness which she attributes to diabetes. No nausea, vomiting, or appetite changes.

Past nephrology history: CKD Stage 3a diagnosed 2019, attributed to diabetic and hypertensive nephropathy. No prior AKI episodes. No kidney stones. No family history of ESRD.

OBJECTIVE:
Vitals: BP 146/88 mmHg (target <130/80 given CKD + DM), HR 70 bpm, Weight 240 lbs.
General: Obese female, well-appearing.
Cardiovascular: RRR, no murmurs. No JVD.
Abdomen: Soft, non-tender. No CVA tenderness bilaterally.
Extremities: Trace pretibial edema bilaterally.

Labs reviewed (drawn 06/30/2025):
- Creatinine: 1.3 mg/dL (H), BUN 20
- eGFR: 58 mL/min/1.73m² — CKD Stage 3a, stable from prior
- Urine Albumin/Creatinine Ratio: 48 mg/g (microalbuminuria, A2 category)
- Cystatin C: 1.22 mg/L (mildly elevated)
- Phosphorus: 4.2 mg/dL (normal)
- Intact PTH: 72 pg/mL (mildly elevated, consistent with early CKD-MBD)
- CO2: 23 mEq/L (low-normal, monitor for metabolic acidosis)

ASSESSMENT:
1. Chronic kidney disease, Stage 3a (N18.3) — eGFR 58, stable. Etiology: diabetic nephropathy and hypertensive nephrosclerosis. Microalbuminuria persistent at 48 mg/g on maximized ACE inhibitor therapy. No evidence of progression over past 12 months. Cystatin C-based eGFR concordant.
2. Diabetic nephropathy — Microalbuminuria category A2 despite max-dose ACE inhibitor. Consider SGLT2 inhibitor for additional renal protection (patient already on Jardiance — beneficial).
3. Hypertension, suboptimally controlled for CKD — BP 146/88 today, goal <130/80 per KDIGO. Dual therapy with lisinopril 40mg + amlodipine 10mg. May need addition of third agent.
4. Early CKD-mineral bone disease — PTH mildly elevated at 72. Phosphorus normal. Monitor, no treatment indicated at this stage.

PLAN:
1. Continue lisinopril 40mg daily — maximized for renal protection. Do not discontinue.
2. Jardiance (empagliflozin) — confirmed, continue for cardiorenal benefit per CREDENCE/EMPA-REG data.
3. Low-protein diet reinforced: 0.8 g/kg/day protein target. Provided printed dietary guidelines. Discussed importance for slowing CKD progression.
4. BP goal <130/80: Discussed with PCP Dr. Chen — consider adding chlorthalidone if BP persists above goal. Avoid NSAIDs.
5. Avoid nephrotoxic medications — NSAIDs, IV contrast (if contrast needed, use low-osmolar with pre-hydration protocol). Updated allergy/precaution list.
6. Labs in 6 months: BMP, UACR, CBC, phosphorus, PTH, Vitamin D 25-OH.
7. Referral thresholds discussed with patient: if eGFR drops below 30, will begin pre-ESRD education and vascular access planning. Current trajectory does not suggest near-term need.
8. Return to nephrology clinic in 6 months. Call for acute creatinine rise >25% from baseline.`,
  },
];

// ─── Patient 2: Dorothy Williams (CRN-PAT-00715) ─────────────────────────────
// 80F, Medicare FFS, Geriatric Medicine — complex elderly patient

export const DOROTHY = {
  id: 'CRN-PAT-00715',
  name: 'Williams, Dorothy A.',
  short: 'Williams, D.',
  gender: 'F', age: '80y', dob: '06/22/1945',
  mrn: '00715', plan: 'Medicare FFS',
  encounter: 'Chronic Care Management', date: '04/21/2026',
  physician: 'Dr. Michael Torres, MD',
};

export const DOROTHY_VITALS = [
  { date: '04/21/2026', bp: '132/78', hr: '74 reg', rr: '18', temp: '97.8°F', spo2: '93%', wt: '124 lbs', bmi: '18.2', pain: '3/10' },
  { date: '01/20/2026', bp: '138/82', hr: '76 reg', rr: '17', temp: '98.0°F', spo2: '94%', wt: '132 lbs', bmi: '19.4', pain: '2/10' },
  { date: '10/15/2025', bp: '142/84', hr: '72 reg', rr: '16', temp: '98.2°F', spo2: '96%', wt: '142 lbs', bmi: '20.9', pain: '1/10' },
];

export const DOROTHY_CONDITIONS = [
  { icd: 'I50.22', display: 'Chronic systolic congestive heart failure', status: 'Active', onset: '2022-08-14' },
  { icd: 'N18.4',  display: 'Chronic kidney disease, stage 4',            status: 'Active', onset: '2021-03-22' },
  { icd: 'I10',    display: 'Essential hypertension',                      status: 'Active', onset: '2010-06-01' },
  { icd: 'E78.5',  display: 'Dyslipidemia, unspecified',                   status: 'Active', onset: '2015-09-12' },
  { icd: 'M81.0',  display: 'Osteoporosis without pathological fracture',  status: 'Active', onset: '2018-11-30' },
  { icd: 'G30.1',  display: "Alzheimer's disease with late onset",         status: 'Active', onset: '2023-04-10' },
];

export const DOROTHY_MEDICATIONS = [
  { name: 'Furosemide 40 mg tablet',       sig: 'Take 1 tablet by mouth once daily', refills: 5, date: '2026-01-20' },
  { name: 'Carvedilol 12.5 mg tablet',     sig: 'Take 1 tablet by mouth twice daily', refills: 5, date: '2026-01-20' },
  { name: 'Lisinopril 5 mg tablet',        sig: 'Take 1 tablet by mouth once daily', refills: 5, date: '2025-10-15' },
  { name: 'Digoxin 0.0625 mg tablet',     sig: 'Take 1 tablet by mouth once daily (renal-adjusted)', refills: 5, date: '2025-08-14' },
  { name: 'Atorvastatin 40 mg tablet',     sig: 'Take 1 tablet by mouth at bedtime', refills: 3, date: '2025-10-15' },
  { name: 'Potassium Chloride 20 mEq tablet', sig: 'Take 1 tablet by mouth once daily with food', refills: 5, date: '2025-08-14' },
  { name: 'Alendronate 70 mg tablet',      sig: 'Take 1 tablet weekly on empty stomach', refills: 3, date: '2024-11-08' },
  { name: 'Donepezil 5 mg tablet',         sig: 'Take 1 tablet by mouth at bedtime', refills: 5, date: '2023-04-15' },
  { name: 'Cyanocobalamin 1000 mcg IM',    sig: 'Inject intramuscularly once monthly', refills: 11, date: '2025-02-15' },
];

export const DOROTHY_LABS = [
  { name: 'Creatinine',         value: '3.4 mg/dL',   ref: '0.5–1.1',  flag: 'H', date: '2026-04-10', note: 'CKD Stage 4' },
  { name: 'eGFR',               value: '22 mL/min',   ref: '> 60',     flag: 'L', date: '2026-04-10', note: 'Stage 4 range 15–29' },
  { name: 'Albumin',            value: '3.1 g/dL',    ref: '3.5–5.0',  flag: 'L', date: '2026-04-10', note: 'Hypoalbuminemia' },
  { name: 'Prealbumin',         value: '14 mg/dL',    ref: '18–45',    flag: 'L', date: '2026-04-10', note: 'Protein deficiency' },
  { name: 'BNP',                value: '412 pg/mL',   ref: '< 100',    flag: 'H', date: '2026-04-10', note: 'Elevated CHF marker' },
  { name: 'Potassium',          value: '4.8 mEq/L',   ref: '3.5–5.0',  flag: '',  date: '2026-04-10', note: '' },
  { name: 'Total Cholesterol',  value: '162 mg/dL',   ref: '< 200',    flag: '',  date: '2026-01-20', note: '' },
  { name: 'PHQ-9 Score',        value: '18 / 27',     ref: '< 5',      flag: 'H', date: '2026-03-15', note: 'Severe depression' },
];

export const DOROTHY_NOTES = [
  {
    date: '04/21/2026',
    type: 'Chronic Care Management Visit',
    provider: 'Dr. Michael Torres, MD',
    excerpt: `SUBJECTIVE:
Mrs. Williams is an 80-year-old female presenting for monthly CCM check-in. Her daughter, who accompanies her today, reports continued decline in appetite and increasing fatigue over the past 2–3 months. Patient states she "doesn't feel like eating" and has been skipping meals frequently, sometimes only eating one small meal per day. She reports unintentional weight loss — clothes are looser. She denies nausea or vomiting but endorses early satiety. She reports feeling "sad and empty" but when asked directly about depression, she says "I'm just old, that's all." She endorses poor sleep, waking frequently at night. Daughter reports patient has been less engaged socially, stopped attending her weekly bridge group, and sits in her chair most of the day. Daughter also notes worsening short-term memory — patient forgot to take her medications twice this week and left the stove on once.

Regarding CHF: patient denies worsening dyspnea, orthopnea, or PND. She reports mild bilateral ankle swelling that improves overnight. She has been adherent to fluid restriction (1.5L/day per daughter). No recent hospitalizations. No chest pain or palpitations.

Regarding CKD: no new urinary symptoms. No gross hematuria. Reports mild bilateral lower back ache that she has had chronically.

OBJECTIVE:
Vitals: BP 132/78 mmHg, HR 74 bpm regular, RR 18, Temp 97.8°F, SpO2 93% on RA (baseline 93–94%), Weight 124 lbs (down from 132 lbs on 01/20/2026 and 142 lbs on 10/15/2025), BMI 18.2 kg/m².
General: Thin, frail-appearing elderly female. Appears older than stated age. Temporal wasting noted. Alert but affect is flat. Makes limited eye contact.
HEENT: Mucous membranes dry. Poor dentition — several missing molars bilaterally. Conjunctivae pale.
Cardiovascular: Regular rate and rhythm. S1/S2 normal. S3 gallop present. No murmurs. JVP estimated at 8 cm H₂O (mildly elevated).
Pulmonary: Bibasilar fine crackles, left > right. No wheezes.
Abdomen: Soft, mildly distended. Normoactive bowel sounds. No tenderness. No hepatomegaly.
Extremities: 1+ pitting edema bilateral ankles. Skin thin and fragile. No ulcers.
Musculoskeletal: Thoracic kyphosis. Grip strength reduced bilaterally. Timed Up and Go test: 18 seconds (high fall risk, >12 seconds).
Neuropsych: Oriented to person and place, not date (states "March" — it is April). Recall 1/3 objects at 5 minutes. Flat affect. Speech slow but coherent. No psychomotor agitation.

Labs reviewed (drawn 04/10/2026):
- Creatinine: 3.4 mg/dL (H, up from 3.3 in January), BUN 42 (H)
- eGFR: 22 mL/min — CKD Stage 4, declined from 23 in January
- Albumin: 3.1 g/dL (L) — down from 3.4 in January
- Prealbumin: 14 mg/dL (L) — indicative of protein-calorie malnutrition
- BNP: 412 pg/mL (H) — slightly up from 405 in January
- Potassium: 4.8 mEq/L — upper normal, up from 4.5 in January, monitor given CKD-4 + ACE-I
- Hemoglobin: 10.2 g/dL (L) — declining from 10.6 in January, anemia of CKD worsening
- PHQ-9 (03/15/2026): Score 18/27 — moderately severe depression

Imaging reviewed:
- Echocardiogram (02/18/2026): LVEF 35% (previously 40% on 08/2025), moderate LV dilation, moderate mitral regurgitation, estimated RVSP 48 mmHg. Worsening systolic function.

ASSESSMENT:
1. Chronic systolic heart failure, NYHA Class III (I50.22) — LVEF declined from 40% → 35%. BNP 412, up from 405 in January — echo shows worsening systolic function. Bibasilar crackles and mild volume overload on exam. Currently compensated. S3 present, consistent with reduced EF.
2. Chronic kidney disease, Stage 4 (N18.4) — eGFR 22, stable. CKD etiology: hypertensive nephrosclerosis + cardiorenal syndrome. Anemia of CKD present (Hgb 10.2). PTH and phosphorus monitoring needed. Approaching pre-ESRD planning threshold.
3. Protein-calorie malnutrition, moderate — 18 lbs unintentional weight loss over 6 months (142 → 124 lbs, 12.7% loss). BMI 18.2 (underweight). Albumin 3.1 (L), prealbumin 14 (L). Temporal wasting. Meets criteria for moderate malnutrition per ASPEN/AND consensus: inadequate intake, weight loss >5% in 6 months, muscle wasting, reduced grip strength. Code E44.0 (moderate protein-calorie malnutrition).
4. Major depressive disorder, single episode, moderate (F32.1) — PHQ-9 score 18 (moderately severe). Patient endorses anhedonia, fatigue, sleep disturbance, social withdrawal, appetite loss. Flat affect on exam. Daughter corroborates significant functional decline. Depression may be contributing to poor oral intake and weight loss. Requires treatment.
5. Alzheimer's disease with late onset (G30.1) — MMSE 22/30 at last assessment. Worsening short-term memory per family. Oriented to person/place but not date. Medication non-adherence events this week. Donepezil 5mg ongoing.
6. Osteoporosis (M81.0) — High fall risk (TUG 18 sec). Kyphosis present. Alendronate ongoing. DEXA due.
7. Hypertension (I10) — BP 132/78, at goal. Continue current regimen.
8. Dyslipidemia (E78.5) — Continue atorvastatin. Lipid panel due.
9. Anemia of CKD — Hgb 10.2, likely EPO deficiency from CKD-4. Iron studies needed.

PLAN:
1. Heart failure: Continue furosemide 40mg daily, carvedilol 12.5mg BID, lisinopril 5mg daily. Consider uptitrating carvedilol to 25mg BID if BP tolerates — target dose for mortality benefit. Monitor daily weights — instruct daughter. If weight gain >3 lbs in 48 hours, call clinic. Cardiology follow-up in 4 weeks for repeat echo assessment and medication optimization.
2. CKD: Continue nephrology co-management. Recheck BMP in 2 weeks — monitor potassium closely (4.8, near upper limit with ACE-I + CKD-4). If K >5.2, reduce lisinopril. Discuss pre-ESRD education and goals of care at next nephrology visit — patient may not be a dialysis candidate given overall functional status and comorbidities.
3. Malnutrition: Urgent dietitian consultation placed today. Start oral nutritional supplements — Ensure Plus TID between meals. Consider Megace (megestrol) 400mg daily for appetite stimulation if oral supplements insufficient. Recheck albumin and prealbumin in 4 weeks. Meals on Wheels referral if not already in place. Code E44.0.
4. Depression: Initiate sertraline 25mg daily — starting low given renal impairment and age. Titrate to 50mg in 2 weeks if tolerated. Psychiatry consult placed for comprehensive evaluation. Screen for suicidal ideation at every visit. Discussed treatment importance with patient and daughter — depression is treatable and may be contributing to appetite loss and functional decline.
5. Alzheimer's: Continue donepezil 5mg QHS. Discuss medication management assistance with daughter — consider pill organizer or supervised administration. Safety evaluation: remove stove knobs or install auto-shutoff. Reassess cognitive function in 3 months.
6. Fall prevention: Home physical therapy referral. Home safety evaluation. Ensure adequate vitamin D (check 25-OH level). Hip protectors discussed.
7. Anemia: Order iron studies (ferritin, TSAT, reticulocyte count). If iron-replete, likely EPO deficiency — discuss with nephrology regarding ESA therapy.
8. Goals of care: Initiate advance directive conversation at next visit. Daughter is current healthcare proxy.
9. Return to clinic in 2 weeks for medication titration and weight check. Monthly CCM visits continue.`,
  },
  {
    date: '03/15/2026',
    type: 'Comprehensive Geriatric Assessment',
    provider: 'Dr. Michael Torres, MD',
    excerpt: `SUBJECTIVE:
Mrs. Williams presents for comprehensive geriatric assessment. Daughter, primary caregiver, accompanies and provides most of the history. Patient has been "slowing down" over the past several months. Daughter reports progressive weight loss, decreased appetite, reduced activity level, and worsening forgetfulness. Patient fell at home 3 weeks ago — tripped over a rug, landed on her right hip. No fracture per ED evaluation (X-ray negative), but significant bruising and fear of falling since. She has been using a walker since the fall. She reports bilateral knee pain with ambulation, rated 3/10. She denies dizziness or syncope. She sleeps 4–5 hours per night, wakes frequently, lies in bed ruminating. She reports feeling "worthless" and "like a burden" to her daughter. She denies suicidal ideation when asked directly but says "I wouldn't mind not waking up." Safety plan reviewed.

Functional status per daughter: requires assistance with bathing, dressing (buttons/zippers), and meal preparation. Independent with feeding, toileting, and transfers (with walker). No longer drives. Does not manage own finances — daughter handles.

OBJECTIVE:
Vitals: BP 134/76 mmHg, HR 80 bpm regular, RR 18, Temp 97.9°F, SpO2 93% on RA (baseline), Weight 128 lbs (126 lbs at ED visit 02/26, now 128 with mild fluid repletion; down from 142 lbs in October — 14 lbs net over 5 months), BMI 18.8 kg/m².
General: Thin, elderly female appearing older than stated age. Flat affect. Cooperative but slow to respond.

Cognitive screening:
- MMSE: 22/30 — lost points on orientation (date, day of week), recall (1/3 objects), and serial 7s. Consistent with mild-to-moderate dementia.
- Clock drawing test: Numbers crowded into right half of circle (spatial planning error). Hands placed at 2 and 10 when asked for 2:45 — unable to interpret minute hand position. Worse than 04/2023 evaluation (numbers were placed correctly at that time). Progressive visuospatial and executive decline.
- Montreal Cognitive Assessment (MoCA): 18/30 — impaired visuospatial, delayed recall, and abstraction domains.

Depression screening:
- PHQ-9: Score 18/27 (moderately severe) — endorsed nearly every day: little interest/pleasure (3), feeling down (3), sleep disturbance (3), fatigue (3), poor appetite (2), feeling worthless (2), concentration difficulty (2). Denied active suicidal ideation (0).
- Geriatric Depression Scale (GDS-15): 12/15 — consistent with severe depression.

Fall risk assessment:
- Timed Up and Go (TUG): 16 seconds with walker (>12 = high risk)
- Morse Fall Scale: 75 (high risk — history of fall, secondary diagnosis, ambulatory aid, impaired gait)
- Gait assessment: Shuffling gait, shortened stride length, wide base, difficulty with turns. Uses walker.

Malnutrition screening:
- MNA-SF (Mini Nutritional Assessment Short Form): Score 5/14 — malnourished
- Grip strength (right hand): 12 kg (below 16 kg threshold for sarcopenia)
- Mid-arm circumference: 21 cm (below 22 cm — wasting)

ASSESSMENT:
1. Major depressive disorder, moderate (F32.1) — PHQ-9 18, GDS 12/15. Significant functional impact: social withdrawal, anorexia with weight loss, insomnia, anhedonia, feelings of worthlessness. Passive death wish present without active suicidal ideation. Comorbid Alzheimer's complicates diagnosis but degree of mood symptoms exceeds expected cognitive-related apathy. Requires pharmacologic treatment and psychiatry involvement.
2. Alzheimer's disease with late onset (G30.1) — MMSE 22/30, MoCA 18/30. Progressive course. Executive dysfunction on clock drawing. ADL impairments progressing. Currently on donepezil 5mg. May benefit from dose increase or memantine addition.
3. Protein-calorie malnutrition — MNA-SF 5/14 (malnourished). Weight loss 14 lbs over 5 months (9.9%). Reduced grip strength, mid-arm wasting. Contributing factors: depression-related anorexia, poor dentition, cognitive impairment affecting meal preparation. Albumin and prealbumin pending.
4. High fall risk — Recent fall with near-hip fracture. TUG 16 sec, Morse 75. Gait impairment. Sarcopenia contributing. Multifactorial: deconditioning, cognitive impairment, polypharmacy, orthostatic potential.
5. Chronic systolic CHF (I50.22) — Managed. No acute decompensation today.
6. CKD Stage 4 (N18.4) — eGFR 22. Stable per last labs.
7. Osteoporosis (M81.0) — High fracture risk given fall history, low BMI, and sarcopenia. DEXA overdue.

PLAN:
1. Depression: Initiate pharmacotherapy after discussion with daughter — sertraline preferred. Start 25mg daily, titrate slowly. Psychiatry referral placed. Safety plan documented. Follow-up PHQ-9 in 4 weeks.
2. Alzheimer's: Continue donepezil 5mg. Consider increase to 10mg at next visit if tolerated. Evaluate for memantine addition. Caregiver support resources provided to daughter.
3. Malnutrition: Order albumin, prealbumin, B12, folate, iron studies, 25-OH vitamin D. Dietitian referral placed. Start oral supplements. Dental evaluation recommended for denture fitting.
4. Fall prevention: Home PT referral. Home safety evaluation. Remove throw rugs. Adequate lighting. Bathroom grab bars. Review medications for fall risk contributors. Vitamin D supplementation pending level.
5. Labs ordered: CBC, BMP, albumin, prealbumin, B12, folate, iron studies, 25-OH vitamin D, TSH, PTH.
6. Advance directive discussion initiated — daughter amenable, patient agreeable. Will complete at next visit.
7. DEXA scan ordered. Fall risk precautions reviewed with daughter.
8. Return to clinic in 4 weeks for depression reassessment and lab review.`,
  },
  {
    date: '01/20/2026',
    type: 'CHF/CKD Quarterly Follow-up',
    provider: 'Dr. Michael Torres, MD',
    excerpt: `SUBJECTIVE:
Mrs. Williams presents for quarterly follow-up of chronic systolic heart failure and CKD Stage 4. She reports stable exercise tolerance — can walk approximately 50 feet with her walker before needing to rest due to dyspnea. This is unchanged from last visit. She sleeps with 2 pillows, same as before. She denies orthopnea or PND. She reports mild bilateral ankle swelling that is present by evening and resolves overnight. She has been adherent to fluid restriction of 1.5L/day and low-sodium diet per daughter. She denies chest pain, palpitations, or dizziness. She weighs herself daily — weights have been stable per log. She reports good medication adherence with daughter's assistance.

She reports decreased appetite over the past month — eating about 50% of meals. She reports "food doesn't taste good anymore." She denies nausea or vomiting. No abdominal pain. Regular bowel movements.

Regarding CKD: no new urinary symptoms. She reports nocturia x2, unchanged. No dysuria or hematuria.

She reports feeling more tired than usual and less interested in activities. Daughter notes she has stopped watching her favorite television programs and often sits quietly staring. Daughter is concerned about her mood.

OBJECTIVE:
Vitals: BP 138/82 mmHg, HR 76 bpm regular, RR 17, Temp 98.0°F, SpO2 94% on RA, Weight 132 lbs (down from 142 lbs on 10/15/2025 — 10 lbs over 3 months), BMI 19.4 kg/m².
General: Thin elderly female. Appears fatigued. Cooperative.
Cardiovascular: RRR. S1/S2 present. Faint S3 appreciated. No murmurs. JVP approximately 7 cm H₂O.
Pulmonary: Faint bibasilar crackles, clearing with deep breathing. No wheezes.
Abdomen: Soft, non-tender. No ascites.
Extremities: Trace bilateral ankle edema. Skin intact. No cyanosis.

Labs reviewed (drawn 01/15/2026):
- BNP: 405 pg/mL (H) — stable from 398 in October
- Creatinine: 3.3 mg/dL (H), BUN 40
- eGFR: 23 mL/min — CKD Stage 4, stable
- Potassium: 4.5 mEq/L — acceptable on current regimen
- Albumin: 3.4 g/dL (low-normal)
- Hemoglobin: 10.6 g/dL (L) — mild interval decline, anemia of CKD

Echocardiogram (ordered, scheduled 02/18/2026) — prior echo 08/2025: LVEF 40%, moderate LV dilation, mild MR.

ASSESSMENT:
1. Chronic systolic heart failure, NYHA Class III (I50.22) — Clinically stable. LVEF 40% on last echo (08/2025). BNP 405, stable from 398 in October. Mild volume overload — bibasilar crackles but compensated. No hospitalizations since August 2025. Repeat echo ordered to trend function.
2. Chronic kidney disease, Stage 4 (N18.4) — eGFR 23, stable. Cardiorenal physiology. Potassium stable at 4.5 on low-dose ACE-I. Anemia of CKD present, Hgb 10.6.
3. Unintentional weight loss — 10 lbs over 3 months (7%). Albumin 3.4, borderline. Appetite declining. Multifactorial: possible depression, CHF-related cachexia, CKD-related uremic anorexia. Monitor closely — if progressive, formal malnutrition workup indicated.
4. Possible depressive disorder — Daughter reports anhedonia, social withdrawal, fatigue beyond cardiac limitation. Consider formal screening at next visit — PHQ-9 and GDS.
5. Hypertension (I10) — BP 138/82, acceptable for this patient given CKD-4 and CHF hemodynamics. Avoid aggressive lowering.
6. Alzheimer's disease (G30.1) — Stable per daughter. No behavioral disturbances. Donepezil 5mg continued.
7. Osteoporosis (M81.0) — Alendronate continued. DEXA due.

PLAN:
1. Heart failure: Continue current regimen — furosemide 40mg daily, carvedilol 12.5mg BID, lisinopril 5mg daily. Daily weights. Fluid restriction 1.5L/day. Low-sodium diet. Echocardiogram scheduled 02/18/2026 — will assess for interval change in EF.
2. CKD: Continue monitoring. Recheck BMP in 6 weeks. Monitor potassium. Nephrology follow-up in 2 months. Discussed with patient and daughter that if eGFR continues to decline, goals-of-care conversation regarding dialysis vs. conservative management will be important.
3. Weight loss/nutrition: Monitor closely. Encouraged calorie-dense foods. Small, frequent meals. If weight continues to decline, will order prealbumin and initiate formal malnutrition assessment. Consider dietitian referral.
4. Mood: Schedule comprehensive geriatric assessment including PHQ-9, GDS, and updated MMSE. Daughter agrees this is needed.
5. Anemia: Stable. Recheck CBC with reticulocyte count at next visit. Discuss ESA therapy with nephrology if Hgb drops below 10.
6. Continue all other medications unchanged. Reviewed medication list for interactions and renal dosing — all appropriate.
7. Return to clinic in 8 weeks. Sooner if weight gain >3 lbs/48 hrs, worsening dyspnea, or new symptoms.`,
  },
];

export const DOROTHY_ORDERS = [
  { name: 'Dietitian Consultation',        type: 'Referral',  status: 'Ordered',    date: '2026-04-21', provider: 'Dr. Torres' },
  { name: 'Psychiatry Consult',            type: 'Referral',  status: 'Pending',    date: '2026-04-21', provider: 'Dr. Torres' },
  { name: 'BMP + Albumin + Prealbumin',   type: 'Lab',       status: 'Completed',  date: '2026-04-10', provider: 'Dr. Torres' },
  { name: 'BNP',                           type: 'Lab',       status: 'Completed',  date: '2026-04-10', provider: 'Dr. Torres' },
  { name: 'Echocardiogram',               type: 'Imaging',   status: 'Completed',  date: '2026-02-18', provider: 'Dr. Torres' },
  { name: 'Fall Risk Assessment',          type: 'Screening', status: 'Completed',  date: '2026-03-15', provider: 'Dr. Torres' },
];

// ─── Margaret Thompson (existing patient — schedule metadata only) ────────────

export const MARGARET = {
  id: 'CRN-PAT-00482',
  name: 'Thompson, Margaret E.',
  short: 'Thompson, M.',
  gender: 'F', age: '77y', dob: '03/12/1949',
  mrn: '00482', plan: 'Medicare Advantage — Aetna',
  encounter: 'Annual Wellness Visit', date: '04/21/2026',
  physician: 'Dr. Sarah Chen, MD',
};

export const CERNER_ORDERS = [
  { name: 'BMP (Basic Metabolic Panel)', type: 'Lab',        status: 'Completed', date: '2026-04-10', provider: 'Dr. Chen' },
  { name: 'HbA1c',                       type: 'Lab',        status: 'Completed', date: '2026-01-15', provider: 'Dr. Chen' },
  { name: 'Lipid Panel',                 type: 'Lab',        status: 'Completed', date: '2025-10-22', provider: 'Dr. Chen' },
  { name: 'Urine Albumin/Creatinine',    type: 'Lab',        status: 'Completed', date: '2025-10-22', provider: 'Dr. Chen' },
  { name: 'Cardiology Referral',         type: 'Referral',   status: 'Pending',   date: '2026-04-21', provider: 'Dr. Chen' },
  { name: 'Depression Screening (PHQ-9)',type: 'Screening',  status: 'Ordered',   date: '2026-04-21', provider: 'Dr. Chen' },
  { name: 'CPAP Compliance Check',       type: 'DME Review', status: 'Ordered',   date: '2026-04-21', provider: 'Dr. Chen' },
];

// ─── Chart Review Documents — Margaret Thompson ─────────────────────────────
// Diverse document types: imaging, lab reports, consults, procedures, ED visits, screenings

export const CERNER_CHART_DOCS = [
  {
    date: '04/10/2026',
    type: 'Lab Report — Basic Metabolic Panel',
    category: 'Lab' as const,
    provider: 'Central Laboratory Services',
    excerpt: `BASIC METABOLIC PANEL (BMP)
Ordering Provider: Dr. Sarah Chen, MD
Collection Date/Time: 04/10/2026  07:42 AM
Received: 04/10/2026  08:15 AM
Reported: 04/10/2026  10:03 AM
Specimen: Serum, Fasting

RESULTS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Test                    Result      Reference Range     Flag
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Glucose, Fasting        186 mg/dL   70–100 mg/dL        HIGH
BUN                     22 mg/dL    7–25 mg/dL
Creatinine              1.4 mg/dL   0.5–1.1 mg/dL       HIGH
eGFR (CKD-EPI)          52 mL/min   > 60 mL/min         LOW
Sodium                  138 mEq/L   136–145 mEq/L
Potassium               4.1 mEq/L   3.5–5.0 mEq/L
Chloride                102 mEq/L   98–106 mEq/L
CO2 (Bicarbonate)       24 mEq/L    22–29 mEq/L
Calcium                 9.2 mg/dL   8.5–10.5 mg/dL
Anion Gap               12          8–16
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

INTERPRETATION:
Elevated fasting glucose consistent with poorly controlled type 2 diabetes mellitus. Creatinine elevated at 1.4 mg/dL with eGFR 52 mL/min, consistent with CKD Stage 3a (stable from prior at 55 mL/min on 01/2026). No significant electrolyte abnormalities. Bicarbonate normal — no metabolic acidosis at this time.

CRITICAL VALUES: None
DELTA CHECK: Creatinine increased from 1.3 mg/dL (01/15/2026) — mild interval increase, within expected variation.

Reported by: Jennifer Martinez, MT(ASCP)
Verified by: Dr. Alan Foster, MD — Laboratory Director`,
  },
  {
    date: '01/15/2026',
    type: 'Lab Report — Hemoglobin A1c',
    category: 'Lab' as const,
    provider: 'Central Laboratory Services',
    excerpt: `HEMOGLOBIN A1C / GLYCATED HEMOGLOBIN
Ordering Provider: Dr. Sarah Chen, MD
Collection Date/Time: 01/15/2026  07:30 AM
Reported: 01/15/2026  11:45 AM
Specimen: Whole Blood (EDTA)
Method: HPLC (NGSP certified, DCCT standardized)

RESULTS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Test                    Result      Reference Range     Flag
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Hemoglobin A1c          8.2%        < 5.7% (normal)     HIGH
                                    5.7–6.4% (prediab)
                                    ≥ 6.5% (diabetes)
Estimated Avg Glucose   189 mg/dL   (calculated)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

TRENDING:
Date            A1c     eAG
01/15/2026      8.2%    189 mg/dL   ← Current
07/02/2025      8.9%    209 mg/dL
01/08/2025      8.5%    197 mg/dL
07/15/2024      7.8%    177 mg/dL

INTERPRETATION:
Hemoglobin A1c of 8.2% indicates suboptimal glycemic control in a patient with established type 2 diabetes mellitus. Improved from 8.9% (07/2025). Estimated average glucose 189 mg/dL. ADA recommends individualized A1c target — for this patient (age 77, multiple comorbidities), a target of <8.0% may be appropriate per ADA 2026 Standards of Care, though <7.5% is preferable if achievable without hypoglycemia.

Reported by: Sandra Liu, MT(ASCP)
Verified by: Dr. Alan Foster, MD — Laboratory Director`,
  },
  {
    date: '01/08/2026',
    type: 'Chest X-ray — PA and Lateral',
    category: 'Imaging' as const,
    provider: 'Dr. Amanda Torres, MD — Radiology',
    excerpt: `CHEST RADIOGRAPH — PA AND LATERAL VIEWS

Patient: Thompson, Margaret E.     MRN: 00482
Date of Study: 01/08/2026
Ordering Provider: Dr. Sarah Chen, MD
Indication: Annual screening. History of hypertension, obesity, OSA. Pre-operative clearance for potential future procedures.
Comparison: Chest X-ray 01/12/2025

FINDINGS:

Lungs: Clear bilaterally. No focal consolidation, mass, or nodule. No pleural effusion. No pneumothorax. Lung volumes are mildly diminished, likely related to body habitus and low inspiratory effort.

Heart: Cardiac silhouette is mildly enlarged. Cardiothoracic ratio approximately 0.55 (mildly elevated; normal < 0.50). Stable compared to prior. Consistent with known left ventricular hypertrophy.

Mediastinum: Aorta is mildly tortuous and calcified — atherosclerotic changes. Mediastinal contours otherwise normal. No lymphadenopathy.

Pleura: No pleural effusion or thickening.

Bones: Mild degenerative changes of the thoracic spine. No acute fracture or lytic lesion. Mild osteopenia.

Soft Tissues: Large body habitus limits evaluation. No subcutaneous emphysema.

IMPRESSION:
1. Mild cardiomegaly, stable — consistent with known LVH/hypertensive heart disease.
2. No acute cardiopulmonary disease.
3. Aortic atherosclerotic calcifications — chronic.
4. Mild thoracic degenerative changes and osteopenia.

Electronically signed: Amanda Torres, MD
Board Certified — Diagnostic Radiology`,
  },
  {
    date: '10/22/2025',
    type: 'Lab Report — Lipid Panel & Urine Albumin',
    category: 'Lab' as const,
    provider: 'Central Laboratory Services',
    excerpt: `LIPID PANEL, FASTING + URINE ALBUMIN-TO-CREATININE RATIO
Ordering Provider: Dr. Sarah Chen, MD
Collection Date/Time: 10/22/2025  07:55 AM
Reported: 10/22/2025  12:30 PM
Specimen: Serum (fasting 12 hrs confirmed) + Random Urine

LIPID PANEL:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Test                    Result      Reference/Goal      Flag
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Total Cholesterol       198 mg/dL   < 200 mg/dL
LDL Cholesterol (calc)  112 mg/dL   < 70 mg/dL*         HIGH
HDL Cholesterol         42 mg/dL    > 60 mg/dL          LOW
Triglycerides           188 mg/dL   < 150 mg/dL         HIGH
VLDL (calc)             38 mg/dL    5–40 mg/dL
Non-HDL Cholesterol     156 mg/dL   < 100 mg/dL*        HIGH
Total/HDL Ratio         4.7         < 3.5 optimal       HIGH
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
*Goals based on ASCVD high-risk category (diabetes + CKD + HTN)

URINE ALBUMIN-TO-CREATININE RATIO (UACR):
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Urine Albumin           8.6 mg/dL
Urine Creatinine        179 mg/dL
Albumin/Creatinine      48 mg/g     < 30 mg/g           HIGH
                                    (Microalbuminuria: 30–300)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

INTERPRETATION:
Mixed dyslipidemia with LDL significantly above goal for this high-risk patient. HDL low. Triglycerides mildly elevated — consider contribution of uncontrolled diabetes. Current therapy: atorvastatin 40mg — intensification recommended.

Urine albumin-to-creatinine ratio 48 mg/g — persistent microalbuminuria (KDIGO Category A2). Consistent with early diabetic nephropathy and/or hypertensive nephrosclerosis. Maximized ACE inhibitor therapy is appropriate for renal protection. SGLT2 inhibitor (Jardiance) provides additional renoprotective benefit.

Reported by: Sandra Liu, MT(ASCP)
Verified by: Dr. Alan Foster, MD — Laboratory Director`,
  },
  {
    date: '08/18/2025',
    type: 'Ophthalmology — Diabetic Retinal Examination',
    category: 'Consult' as const,
    provider: 'Dr. Patricia Nguyen, OD — Optometry',
    excerpt: `COMPREHENSIVE DIABETIC EYE EXAMINATION

Patient: Thompson, Margaret E.     MRN: 00482
Date of Exam: 08/18/2025
Referring Provider: Dr. Sarah Chen, MD
Indication: Annual diabetic retinal screening. Type 2 diabetes mellitus, 10+ year duration.
Last Eye Exam: 08/2024 — mild NPDR OU

HISTORY:
Patient reports stable vision. Denies floaters, flashes, or curtain-like visual field loss. Wears bifocal glasses for reading. Reports occasional dry eyes. No eye pain or redness.

EXAMINATION:

Visual Acuity (corrected):
- OD (Right): 20/30
- OS (Left): 20/25
- Both: 20/25

Intraocular Pressure (Goldmann):
- OD: 16 mmHg (normal < 21)
- OS: 15 mmHg

Pupillary Exam: PERRL, no APD.
Anterior Segment: Mild nuclear sclerotic cataracts OU (2+ NS). Anterior chambers deep and quiet.

DILATED FUNDUS EXAMINATION (Tropicamide 1% + Phenylephrine 2.5%):

Right Eye (OD):
- Optic disc: 0.3 C/D ratio, healthy rim, no pallor or edema
- Macula: Flat, no clinically significant macular edema (CSME)
- Vessels: Scattered dot-blot hemorrhages in temporal periphery (3 quadrants). Rare microaneurysms inferior arcade. No neovascularization.
- Periphery: No tears, holes, or detachment

Left Eye (OS):
- Optic disc: 0.3 C/D ratio, normal
- Macula: Flat, no CSME
- Vessels: Scattered microaneurysms and rare dot hemorrhages temporal and inferior. One cotton-wool spot inferior to disc. No neovascularization.
- Periphery: Unremarkable

OCT Macula (Zeiss Cirrus):
- OD: Central subfield thickness 268 μm (normal < 300). No intraretinal fluid.
- OS: Central subfield thickness 272 μm. No intraretinal fluid.
- Retinal layers well-defined OU.

FUNDUS PHOTOGRAPHY: Digital color and red-free images obtained for documentation. Consistent with clinical findings.

ASSESSMENT:
1. MILD NON-PROLIFERATIVE DIABETIC RETINOPATHY (NPDR), bilateral — E11.319
   Stable from 08/2024 examination. Scattered microaneurysms and dot-blot hemorrhages. One cotton-wool spot OS suggesting focal ischemia. No macular edema by exam or OCT. No proliferative changes.
2. Early nuclear sclerotic cataracts, bilateral — H25.10
   Mild, not visually significant at this time. Monitoring.
3. Dry eye syndrome, mild — H04.129

PLAN:
1. Continue current diabetes management with PCP. Improved glycemic control will slow retinopathy progression. Target A1c <8% appropriate for age.
2. Repeat dilated fundus examination in 12 months. If retinopathy progresses to moderate NPDR or CSME develops, shorten interval to 6 months and consider retinal specialist referral.
3. Artificial tears PRN for dry eyes (preservative-free recommended).
4. Cataracts: Monitor. Discuss surgical evaluation when vision impacts daily function.
5. Report sent to Dr. Sarah Chen.

Electronically signed: Patricia Nguyen, OD, FAAO`,
  },
  {
    date: '07/08/2025',
    type: 'Nephrology Consultation',
    category: 'Consult' as const,
    provider: 'Dr. James Park, MD — Nephrology',
    excerpt: `NEPHROLOGY CONSULTATION NOTE

Patient: Thompson, Margaret E.     MRN: 00482
Date of Service: 07/08/2025
Referring Provider: Dr. Sarah Chen, MD
Reason for Consultation: Ongoing management of CKD Stage 3a with microalbuminuria in the setting of type 2 diabetes and hypertension.

SUBJECTIVE:
Mrs. Thompson is a 76-year-old female referred for ongoing nephrology management. She reports no new urinary symptoms — denies hematuria, dysuria, frequency changes, or flank pain. She states she tries to drink adequate water but admits to not following the low-protein diet recommendations consistently. She reports good medication adherence. She endorses bilateral foot numbness attributed to diabetes. No nausea, vomiting, or appetite changes. No foamy urine. No recent NSAID use.

Past nephrology history: CKD Stage 3a diagnosed 2019, attributed to diabetic and hypertensive nephropathy. No prior AKI episodes. No kidney stones. No family history of polycystic kidney disease or ESRD. Never on dialysis.

OBJECTIVE:
Vitals: BP 146/88 mmHg (target <130/80 given CKD + DM), HR 70 bpm, Weight 240 lbs.
General: Obese female, well-appearing.
Cardiovascular: RRR, no murmurs. No JVD.
Abdomen: Soft, non-tender. No CVA tenderness bilaterally. No palpable kidneys.
Extremities: Trace pretibial edema bilaterally. No skin breakdown.

Labs reviewed (drawn 06/30/2025):
- Creatinine: 1.3 mg/dL (H), BUN 20
- eGFR (CKD-EPI): 58 mL/min/1.73m² — CKD Stage 3a, stable
- Urine Albumin/Creatinine Ratio: 48 mg/g (microalbuminuria, KDIGO A2)
- Cystatin C: 1.22 mg/L (mildly elevated, concordant with creatinine-based eGFR)
- Phosphorus: 4.2 mg/dL (normal)
- Intact PTH: 72 pg/mL (mildly elevated, consistent with early CKD-MBD)
- 25-OH Vitamin D: 28 ng/mL (insufficient, goal >30)
- CO2: 23 mEq/L (low-normal, monitor for metabolic acidosis progression)
- Serum Albumin: 3.8 g/dL (normal)
- CBC: Hgb 12.2, MCV 88 — no anemia currently

Renal Ultrasound (03/2024): Kidneys bilateral normal size (R: 10.8 cm, L: 10.5 cm). Increased cortical echogenicity bilaterally consistent with medical renal disease. No hydronephrosis, stones, or masses.

ASSESSMENT:
1. Chronic kidney disease, Stage 3a (N18.3) — eGFR 58 mL/min, stable over 12 months. Etiology: combined diabetic nephropathy and hypertensive nephrosclerosis. Risk factors: 10+ year T2DM, longstanding poorly controlled HTN, obesity. Microalbuminuria persistent (UACR 48 mg/g, KDIGO Category A2) despite maximized ACE inhibitor. KDIGO risk category: High (G3a A2) — progression risk to ESRD approximately 2.4%/year.
2. Microalbuminuria, persistent — UACR 48 mg/g on max-dose ACE-I. Patient is on empagliflozin (Jardiance) which provides additional renoprotective benefit per EMPA-REG OUTCOME and CREDENCE data. No further antiproteinuric agents indicated at this time.
3. Hypertension, suboptimally controlled for CKD — BP 146/88 today, goal <130/80 per KDIGO 2024 guidelines. Dual therapy with lisinopril 40mg + amlodipine 10mg. Third agent may be required.
4. Early CKD-mineral bone disease — PTH mildly elevated at 72. Phosphorus normal. Vitamin D insufficient at 28. No indication for active vitamin D analog at this stage.

PLAN:
1. Continue lisinopril 40mg daily — maximized dose, do not discontinue or reduce given renoprotective benefit.
2. Continue empagliflozin (Jardiance) — confirmed cardiorenal benefit. Do not discontinue for eGFR >20.
3. Low-protein diet: Target 0.8 g/kg/day (approximately 87 g/day for this patient). Provided printed dietary guide. Dietitian referral recommended.
4. Vitamin D3 supplementation: Start cholecalciferol 2000 IU daily. Recheck 25-OH in 3 months.
5. BP goal <130/80: Communicate with PCP Dr. Chen — consider adding chlorthalidone 12.5mg if BP persists above goal. Avoid NSAIDs strictly.
6. Nephrotoxin avoidance counseling: No NSAIDs (ibuprofen, naproxen), minimize IV contrast (use low-osmolar with pre/post hydration if unavoidable), avoid herbal supplements with renal toxicity risk.
7. Labs in 6 months: BMP, UACR, CBC, phosphorus, PTH, 25-OH vitamin D, lipid panel.
8. CKD education: Discussed disease trajectory. At current eGFR slope (-2 to -3 mL/min/year), anticipated Stage 4 (eGFR <30) in approximately 8–10 years. If eGFR approaches 30, will initiate pre-ESRD education, AV fistula planning, and transplant evaluation discussion. Patient understands.
9. Return to nephrology in 6 months. Call for acute creatinine rise >25% from baseline.

Electronically signed: James Park, MD, FASN
Board Certified — Nephrology, Internal Medicine`,
  },
  {
    date: '03/14/2025',
    type: 'Echocardiogram — Transthoracic',
    category: 'Imaging' as const,
    provider: 'Dr. Robert Kim, MD — Cardiology',
    excerpt: `TRANSTHORACIC ECHOCARDIOGRAM REPORT

Patient: Thompson, Margaret E.     MRN: 00482
Date of Study: 03/14/2025          DOB: 03/12/1949
Referring Physician: Dr. Sarah Chen, MD
Indication: Hypertension, poorly controlled on dual therapy. Evaluate for hypertensive heart disease.
Sonographer: Emily Nakamura, RDCS

TECHNICAL QUALITY: Adequate. All standard views obtained.

FINDINGS:

Left Ventricle:
- LV internal dimension, diastole: 4.8 cm (normal 3.5–5.6 cm)
- LV internal dimension, systole: 3.1 cm
- Interventricular septal thickness, diastole: 1.3 cm (mildly increased; normal 0.6–1.1 cm)
- Posterior wall thickness, diastole: 1.2 cm (mildly increased; normal 0.6–1.1 cm)
- LV mass index: 118 g/m² (elevated; normal < 95 g/m² for females)
- Left ventricular ejection fraction (LVEF): 55% (normal ≥ 55%) — visual estimate and biplane Simpson's concordant
- Wall motion: Normal. No regional wall motion abnormalities.
- LV geometry: Concentric hypertrophy pattern

Diastolic Function:
- Mitral E velocity: 0.7 m/s
- Mitral A velocity: 0.9 m/s
- E/A ratio: 0.78 (impaired relaxation pattern)
- Septal e' velocity: 6 cm/s (reduced)
- Lateral e' velocity: 8 cm/s (reduced)
- Average E/e' ratio: 10 (borderline)
- Tricuspid regurgitation velocity: 2.4 m/s
- LA volume index: 34 mL/m² (mildly dilated; normal < 34 mL/m²)
- GRADE I DIASTOLIC DYSFUNCTION (impaired relaxation)

Right Ventricle:
- RV size and function: Normal
- TAPSE: 22 mm (normal > 17 mm)
- Estimated RVSP: 30 mmHg (normal < 35 mmHg)

Valves:
- Mitral valve: Normal morphology. Trace mitral regurgitation (physiologic).
- Aortic valve: Trileaflet. Mild aortic sclerosis without stenosis. No aortic regurgitation.
- Tricuspid valve: Normal. Trace tricuspid regurgitation used for RVSP estimation.
- Pulmonic valve: Normal.

Pericardium: No pericardial effusion.
Aortic Root: 3.2 cm (normal).
Left Atrium: Mildly dilated at 4.2 cm anteroposterior dimension.

IVC: Normal diameter (1.8 cm) with >50% inspiratory collapse. Estimated RAP 3 mmHg.

IMPRESSION:
1. Normal left ventricular systolic function, LVEF 55%.
2. CONCENTRIC LEFT VENTRICULAR HYPERTROPHY — septal and posterior wall thickening with elevated LV mass index. Consistent with hypertensive heart disease in the setting of longstanding poorly controlled hypertension.
3. Grade I (mild) diastolic dysfunction with impaired relaxation pattern. Average E/e' 10, borderline.
4. Left atrium mildly dilated (4.2 cm) — may reflect chronic diastolic dysfunction and volume overload.
5. Mild aortic sclerosis without hemodynamically significant stenosis.
6. No significant valvular disease. No pericardial effusion.

CLINICAL CORRELATION:
Findings of concentric LVH with diastolic dysfunction are consistent with hypertensive heart disease. Given concurrent CKD Stage 3a, the combination may warrant classification as hypertensive heart disease with chronic kidney disease (I13.10). Recommend blood pressure optimization to goal <130/80 per ACC/AHA guidelines. Follow-up echocardiogram in 1–2 years or sooner if clinical change.

Electronically signed: Robert Kim, MD, FACC
Board Certified — Cardiovascular Disease`,
  },
  {
    date: '09/10/2024',
    type: 'Polysomnography Report — Diagnostic',
    category: 'Procedure' as const,
    provider: 'Dr. Linda Hayes, MD — Pulmonology / Sleep Medicine',
    excerpt: `POLYSOMNOGRAPHY (PSG) — SPLIT-NIGHT STUDY
Sleep Center: Midtown Regional Sleep Disorders Center

Patient: Thompson, Margaret E.     MRN: 00482
Date of Study: 09/10/2024         Lights Out: 22:15   Lights On: 06:00
Referring Provider: Dr. Sarah Chen, MD
Indication: Excessive daytime somnolence, witnessed apneas per bedpartner, BMI 41.5 (morbid obesity), hypertension refractory to 2 agents.

DIAGNOSTIC PORTION (22:15–01:45, 3.5 hours):

Sleep Architecture:
- Total Recording Time: 210 min
- Total Sleep Time: 178 min
- Sleep Efficiency: 84.8%
- Sleep Onset Latency: 8 min
- REM Latency: 92 min
- Stage N1: 12%  |  Stage N2: 62%  |  Stage N3: 8%  |  REM: 18%
- Arousal Index: 38/hr (elevated; normal < 15/hr)

Respiratory Events (Diagnostic):
- Total Apneas: 42 (32 obstructive, 8 mixed, 2 central)
- Total Hypopneas: 56
- Apnea-Hypopnea Index (AHI): 28.0/hr — MODERATE-SEVERE obstructive sleep apnea
- RDI: 32.4/hr
- Oxygen Desaturation Index (ODI): 24/hr
- Lowest SpO2: 82% (during REM supine)
- Mean SpO2: 92%
- Time SpO2 < 90%: 18% of total sleep time
- Supine AHI: 42/hr  |  Non-supine AHI: 16/hr (significant positional component)

Cardiac: Normal sinus rhythm throughout. No significant arrhythmias. Mean sleeping HR 68 bpm.
Limb Movements: Periodic limb movement index 6/hr (normal < 15/hr).

CPAP TITRATION PORTION (01:45–06:00, 4.25 hours):

Titration Protocol: Auto-CPAP range 6–16 cmH₂O, nasal mask
- 8 cmH₂O: AHI 18/hr — residual events
- 10 cmH₂O: AHI 8/hr — improving
- 12 cmH₂O: AHI 2.1/hr — optimal, including REM supine
- Residual AHI at 12 cmH₂O: 2.1/hr (adequate control, <5/hr threshold met)
- Lowest SpO2 on CPAP: 91%
- Mean SpO2 on CPAP: 95%
- Leak: < 24 L/min (acceptable)

Patient Tolerance: Good. Slept 3.8 hours on CPAP. Achieved REM on CPAP.

IMPRESSION:
1. MODERATE-SEVERE OBSTRUCTIVE SLEEP APNEA (AHI 28.0/hr), worse in supine position and during REM sleep. Significant nocturnal hypoxemia with nadir SpO2 82%.
2. CPAP titration successful — optimal pressure 12 cmH₂O with residual AHI 2.1/hr.
3. Reduced deep sleep (N3 8%) — may be related to sleep fragmentation from respiratory events.
4. Morbid obesity (BMI 41.5) is the primary contributing factor. Weight management strongly recommended as adjunctive therapy.

RECOMMENDATIONS:
1. Initiate CPAP therapy at 12 cmH₂O via nasal mask nightly, minimum 4 hours per night.
2. DME setup with heated humidification. Mask fitting — trial nasal pillows if nasal mask intolerant.
3. Compliance download review in 30 and 90 days. Target: ≥70% of nights, ≥4 hours/night for Medicare compliance criteria.
4. Weight loss counseling — even 10% reduction in BMI would significantly reduce AHI.
5. Positional therapy (avoid supine sleeping) as supplement.
6. Follow-up in sleep clinic in 3 months. Repeat PSG only if clinically indicated.

ICD-10: G47.33 — Obstructive sleep apnea (adult)

Electronically signed: Linda Hayes, MD, FCCP
Board Certified — Pulmonary Medicine, Sleep Medicine`,
  },
  {
    date: '06/03/2024',
    type: 'Emergency Department Visit — Hypertensive Urgency',
    category: 'ED' as const,
    provider: 'Dr. Kevin Walsh, MD — Emergency Medicine',
    excerpt: `EMERGENCY DEPARTMENT NOTE

Patient: Thompson, Margaret E.     MRN: 00482
Date/Time of Arrival: 06/03/2024  14:22
Mode of Arrival: Walk-in (daughter drove)
Triage Level: ESI Level 3 (Urgent)
Attending: Dr. Kevin Walsh, MD

CHIEF COMPLAINT: "My blood pressure is very high and I have a bad headache."

HISTORY OF PRESENT ILLNESS:
77-year-old female with history of hypertension, type 2 diabetes, CKD Stage 3, obesity, and OSA presents with frontal headache for 2 days, rated 7/10, accompanied by mild blurred vision. She checked her BP at home pharmacy today and it read 198/110 and was told to go to the ER. She reports adherence to lisinopril 40mg and amlodipine 10mg. She ran out of amlodipine 5 days ago and has not been taking it. She denies chest pain, shortness of breath, palpitations, weakness, speech changes, or focal neurological symptoms. She denies nausea/vomiting. No recent medication changes. No recent illness or increased salt intake (beyond usual).

REVIEW OF SYSTEMS: Positive for headache and blurred vision. Negative for chest pain, dyspnea, focal weakness, speech difficulty, seizure, epistaxis, hematuria.

PAST MEDICAL HISTORY: As per problem list — HTN, T2DM, CKD 3a, OSA on CPAP, morbid obesity, dyslipidemia.
MEDICATIONS: Metformin 1000mg BID, Jardiance 25mg, Atorvastatin 40mg, Lisinopril 40mg, Amlodipine 10mg (non-adherent x5 days), Aspirin 81mg, Gabapentin 300mg TID, CPAP.
ALLERGIES: NKDA
SOCIAL: Non-smoker. No alcohol. Lives alone. Daughter nearby.

PHYSICAL EXAMINATION:
Vitals at Triage: BP 196/108 mmHg (R arm, manual), HR 82 bpm, RR 18, Temp 98.4°F, SpO2 95%, Weight 240 lbs.
Repeat BP (30 min after arrival, lying quietly): 184/102 mmHg.
General: Obese female, uncomfortable with headache, alert and oriented x3.
HEENT: PERRL, EOMI. No papilledema on fundoscopy. Oropharynx clear.
Neck: No JVD, no carotid bruits.
Cardiovascular: Regular rate and rhythm. S1/S2 normal. No murmurs or gallops. No S3.
Pulmonary: Clear to auscultation bilaterally.
Abdomen: Soft, non-tender. No bruits.
Neurological: CN II–XII intact. Strength 5/5 all extremities. Sensation intact. Coordination normal. Gait steady. NIH Stroke Scale: 0.
Extremities: No edema. Pulses intact.

DIAGNOSTIC WORKUP:
- ECG: Normal sinus rhythm, rate 80. LVH by voltage criteria (SV1 + RV5 = 38mm). No ST-T changes. No ischemia.
- BMP: Creatinine 1.3, eGFR 55, K 4.0, Glucose 172 — at baseline
- CBC: WNL
- Troponin I: <0.01 ng/mL (normal)
- Urinalysis: Trace protein, no blood, no infection
- CT Head without contrast: No acute intracranial abnormality. No hemorrhage, mass, or midline shift. Mild periventricular white matter hypodensities consistent with chronic microvascular ischemic changes.

ED COURSE:
Patient observed for 3 hours in the ED. Amlodipine 10mg administered PO. BP trended:
- 14:22  196/108
- 14:55  184/102
- 15:45  168/96
- 17:00  152/90
- 17:30  146/88

Headache improved from 7/10 to 2/10 with acetaminophen 1000mg PO and blood pressure reduction. Visual blurring resolved.

ASSESSMENT:
1. Hypertensive urgency (R03.0) — BP 196/108 secondary to medication non-adherence (5 days without amlodipine). No evidence of end-organ damage: troponin negative, no ECG changes, no neurological deficits, CT head normal, renal function at baseline. This is hypertensive urgency, NOT hypertensive emergency.
2. ECG notable for LVH by voltage criteria — known, correlates with echocardiographic LVH. No acute ischemia.
3. Chronic microvascular ischemic white matter changes on CT — age-appropriate, likely related to longstanding hypertension.

DISPOSITION: Discharge to home. Condition: Stable, Improved.

DISCHARGE INSTRUCTIONS:
1. Resume amlodipine 10mg daily IMMEDIATELY — filled 30-day emergency prescription.
2. Do NOT stop any blood pressure medications without calling your doctor.
3. Home BP monitoring: check BP daily, bring log to next PCP visit.
4. Follow up with Dr. Sarah Chen within 1 week.
5. Return to ED for: BP >180/110 with symptoms, chest pain, weakness on one side, difficulty speaking, worst headache of life, visual loss.
6. Medication adherence counseling provided. Daughter educated on importance of medication refill management.

ICD-10: R03.0 (Elevated blood-pressure reading, without diagnosis of hypertension) — clinical context: hypertensive urgency in known hypertensive

Electronically signed: Kevin Walsh, MD, FACEP`,
  },
];

// ─── Chart Review Documents — Dorothy Williams ──────────────────────────────

export const DOROTHY_CHART_DOCS = [
  {
    date: '04/10/2026',
    type: 'Lab Report — Comprehensive Panel',
    category: 'Lab' as const,
    provider: 'Central Laboratory Services',
    excerpt: `COMPREHENSIVE METABOLIC PANEL + BNP + NUTRITIONAL MARKERS
Ordering Provider: Dr. Michael Torres, MD
Collection Date/Time: 04/10/2026  07:15 AM
Reported: 04/10/2026  11:30 AM
Specimen: Serum, Fasting + Whole Blood (EDTA)

COMPREHENSIVE METABOLIC PANEL:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Test                    Result      Reference Range     Flag
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Glucose, Fasting        102 mg/dL   70–100 mg/dL        HIGH
BUN                     42 mg/dL    7–25 mg/dL          HIGH
Creatinine              3.4 mg/dL   0.5–1.1 mg/dL       HIGH
eGFR (CKD-EPI)          22 mL/min   > 60 mL/min         LOW
Sodium                  136 mEq/L   136–145 mEq/L
Potassium               4.8 mEq/L   3.5–5.0 mEq/L
Chloride                100 mEq/L   98–106 mEq/L
CO2 (Bicarbonate)       20 mEq/L    22–29 mEq/L        LOW
Calcium                 8.8 mg/dL   8.5–10.5 mg/dL
Phosphorus              4.9 mg/dL   2.5–4.5 mg/dL       HIGH
Total Protein           5.8 g/dL    6.0–8.3 g/dL        LOW
Albumin                 3.1 g/dL    3.5–5.0 g/dL        LOW
AST (SGOT)              22 U/L      10–40 U/L
ALT (SGPT)              18 U/L      7–56 U/L
Alk Phos                112 U/L     44–147 U/L
Total Bilirubin         0.8 mg/dL   0.1–1.2 mg/dL
Anion Gap               16          8–16
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

CARDIAC MARKER:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
BNP (Brain Natriuretic)  412 pg/mL  < 100 pg/mL         HIGH
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

NUTRITIONAL MARKERS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Prealbumin (Transthy)    14 mg/dL   18–45 mg/dL         LOW
Transferrin              148 mg/dL  200–360 mg/dL        LOW
Vitamin B12              285 pg/mL  200–900 pg/mL
Folate                   12.4 ng/mL > 3.0 ng/mL
25-OH Vitamin D          18 ng/mL   30–100 ng/mL         LOW
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

HEMATOLOGY:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Hemoglobin               10.2 g/dL  12.0–16.0 g/dL       LOW
Hematocrit               30.6%      36–46%               LOW
MCV                      88 fL      80–100 fL
RDW                      14.8%      11.5–14.5%           HIGH
Reticulocyte Count       0.8%       0.5–2.5%
Iron, Serum              42 mcg/dL  60–170 mcg/dL        LOW
Ferritin                 18 ng/mL   12–150 ng/mL         (low-normal)
TIBC                     380 mcg/dL 250–370 mcg/dL       HIGH
Transferrin Saturation   11%        20–50%               LOW
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

INTERPRETATION:
Advanced CKD Stage 4 with elevated BUN/creatinine and low bicarbonate suggesting early metabolic acidosis. Hyperphosphatemia consistent with CKD-MBD. BNP elevated at 412, stable — chronic systolic heart failure. Hypoalbuminemia (3.1) with low prealbumin (14) and transferrin (148) consistent with moderate protein-calorie malnutrition. Iron deficiency anemia pattern (low serum iron, elevated TIBC, low TSAT 11%) overlapping with anemia of CKD. Vitamin D deficient at 18.

CRITICAL VALUES: None (creatinine elevated but stable from prior)

Reported by: Jennifer Martinez, MT(ASCP)
Verified by: Dr. Alan Foster, MD — Laboratory Director`,
  },
  {
    date: '02/26/2026',
    type: 'Emergency Department Visit — Mechanical Fall',
    category: 'ED' as const,
    provider: 'Dr. Kevin Walsh, MD — Emergency Medicine',
    excerpt: `EMERGENCY DEPARTMENT NOTE

Patient: Williams, Dorothy A.     MRN: 00715
Date/Time of Arrival: 02/26/2026  16:45
Mode of Arrival: EMS — Ambulance (BLS)
Triage Level: ESI Level 3 (Urgent)
Attending: Dr. Kevin Walsh, MD

CHIEF COMPLAINT: Fall at home.

HISTORY OF PRESENT ILLNESS:
80-year-old female with known Alzheimer's disease, CHF, CKD-4, and osteoporosis, brought by EMS after an unwitnessed fall at home. Daughter found patient sitting on the floor next to the living room couch approximately 30 minutes after hearing a "thud." Patient was awake and alert but could not recall what happened. She reports she was trying to get up from the couch without her walker and "my legs gave out." She landed on her right side, striking her right hip and right shoulder. She denies hitting her head. She denies loss of consciousness, chest pain, palpitations, or dizziness preceding the fall. She was unable to get up from the floor without assistance.

Daughter reports this is the patient's 3rd fall in 6 months. Previous falls: (1) tripped over bathroom rug 11/2025, no injury; (2) stumbled getting out of bed 01/2026, bruised left forearm. Patient uses a rolling walker at home but frequently forgets to use it.

REVIEW OF SYSTEMS: Right hip pain (6/10), right shoulder pain (3/10). Denies headache, neck pain, back pain, chest pain, dyspnea. Denies pre-syncopal symptoms.

PHYSICAL EXAMINATION:
Vitals: BP 128/76 mmHg (supine), BP 112/68 mmHg (standing — ORTHOSTATIC positive, >20 mmHg drop), HR 78 bpm (supine), HR 92 bpm (standing), RR 16, Temp 97.6°F, SpO2 93% on RA, Weight 126 lbs.
General: Thin, elderly female. In mild distress secondary to right hip pain. Alert, oriented to person and place (not date or situation).
Head: No scalp hematoma, laceration, or tenderness. No facial trauma.
Neck: No midline cervical tenderness. Full ROM.
Cardiovascular: Regular rate, no murmurs. JVP 6 cm. No peripheral edema today.
Pulmonary: Clear bilaterally, slightly diminished at bases.
Right Hip: Tenderness over greater trochanter. No shortening or external rotation of limb. Passive ROM limited by pain. Able to bear partial weight.
Right Shoulder: Mild tenderness over deltoid. Full ROM with discomfort. Intact rotator cuff strength testing.
Skin: Ecchymosis developing over right lateral hip (5 x 8 cm). Small abrasion right elbow. No skin tears.
Neurological: Oriented x2 (person, place). MMSE not formally scored but significant short-term memory impairment evident. CN intact. Motor: 4/5 bilateral lower extremities (chronic weakness per PCP notes), 5/5 upper extremities. Sensation grossly intact. Gait not tested (pain-limited).

DIAGNOSTIC WORKUP:
- X-ray Right Hip (AP + lateral): No fracture or dislocation. Moderate osteoarthritic changes. Diffuse osteopenia.
- X-ray Right Shoulder (AP): No fracture. Moderate glenohumeral osteoarthritis. Osteopenia.
- X-ray Pelvis (AP): No pelvic fracture. Bilateral hip osteoarthritis. Severe osteopenia.
- CT Head without contrast: No acute intracranial hemorrhage, contusion, or fracture. Bilateral frontoparietal cortical atrophy disproportionate to age — consistent with known Alzheimer's disease. Mild chronic periventricular white matter changes.
- ECG: Normal sinus rhythm, rate 78. No acute changes. Low voltage (consistent with thin habitus).
- BMP: Creatinine 3.3 (at baseline), K 4.7, Glucose 98. No acute derangement.

ASSESSMENT:
1. Mechanical fall, unwitnessed (W19.XXXA) — Patient fell attempting to stand without assistive device. No evidence of syncope, cardiac arrhythmia, or acute neurological event precipitating fall. Significant orthostatic hypotension (20+ mmHg drop) likely contributing factor. Underlying risk factors: Alzheimer's disease (forgets walker, impaired judgment), deconditioning/sarcopenia, osteoporosis, polypharmacy (carvedilol, lisinopril, furosemide — all can contribute to orthostasis).
2. Right hip contusion — no fracture. Remarkable given severe osteopenia/osteoporosis.
3. Orthostatic hypotension — BP drop 128/76 → 112/68 on standing. Likely medication-related (furosemide, carvedilol, lisinopril) compounded by mild dehydration.
4. Alzheimer's disease — contributing to fall risk through impaired safety awareness and forgetting to use walker.

DISPOSITION: Discharge to home with daughter. Condition: Stable.

DISCHARGE INSTRUCTIONS:
1. Right hip: Ice 20 min every 2 hours for 48 hours. Acetaminophen 500mg every 6 hours PRN pain. NO ibuprofen or NSAIDs (CKD).
2. USE WALKER AT ALL TIMES when standing or walking. Do not attempt to stand without it.
3. Fall precautions: Remove throw rugs, ensure adequate lighting, install grab bars in bathroom.
4. Follow up with Dr. Torres within 3–5 days. Discuss medication adjustment for orthostatic hypotension.
5. DEXA scan recommended given severe osteopenia and fall frequency.
6. Return to ED for: inability to bear weight, worsening pain, confusion, headache, vomiting, loss of consciousness.

Electronically signed: Kevin Walsh, MD, FACEP`,
  },
  {
    date: '02/18/2026',
    type: 'Echocardiogram — Transthoracic',
    category: 'Imaging' as const,
    provider: 'Dr. Robert Kim, MD — Cardiology',
    excerpt: `TRANSTHORACIC ECHOCARDIOGRAM REPORT

Patient: Williams, Dorothy A.     MRN: 00715
Date of Study: 02/18/2026          DOB: 06/22/1945
Referring Physician: Dr. Michael Torres, MD
Indication: Chronic systolic heart failure. Interval reassessment of LV function. Prior LVEF 40% (08/2025).
Sonographer: Emily Nakamura, RDCS
Comparison: Echocardiogram 08/12/2025

TECHNICAL QUALITY: Adequate despite thin body habitus. Subcostal views limited.

FINDINGS:

Left Ventricle:
- LV internal dimension, diastole: 6.2 cm (dilated; normal 3.5–5.6 cm) — increased from 5.8 cm on prior
- LV internal dimension, systole: 5.1 cm (dilated)
- Interventricular septal thickness: 0.9 cm (normal)
- Posterior wall thickness: 0.9 cm (normal)
- Left ventricular ejection fraction (LVEF): 35% (reduced; previously 40%) — by biplane Simpson's method
- Wall motion: Global hypokinesis. No discrete regional wall motion abnormality. Apex mildly akinetic.
- LV geometry: Eccentric dilation (dilated cardiomyopathy pattern)

Diastolic Function:
- Mitral E velocity: 1.0 m/s
- Mitral A velocity: 0.5 m/s
- E/A ratio: 2.0 (restrictive filling pattern)
- Septal e' velocity: 4 cm/s (severely reduced)
- Average E/e' ratio: 18 (elevated — consistent with elevated LV filling pressures)
- GRADE III DIASTOLIC DYSFUNCTION (restrictive filling)

Right Ventricle:
- RV mildly dilated
- TAPSE: 14 mm (reduced; normal > 17 mm) — early RV dysfunction
- Estimated RVSP: 48 mmHg (elevated; normal < 35 mmHg)

Valves:
- Mitral valve: Annular dilation. MODERATE MITRAL REGURGITATION — vena contracta 5 mm, regurgitant jet area 6 cm². Functional (secondary to LV dilation and annular stretching). No structural valve abnormality.
- Aortic valve: Trileaflet. Mild sclerosis. No stenosis or regurgitation.
- Tricuspid valve: Mild-moderate tricuspid regurgitation. Used for RVSP estimation.
- Pulmonic valve: Normal.

Left Atrium: Moderately dilated (LA volume index 42 mL/m²; normal < 34).
IVC: Dilated at 2.4 cm with <50% inspiratory collapse. Estimated RAP 10–15 mmHg.
Pericardium: Small pericardial effusion, circumferential, no hemodynamic compromise. Not present on prior study.

IMPRESSION:
1. WORSENING LEFT VENTRICULAR SYSTOLIC DYSFUNCTION — LVEF 35% (declined from 40% on 08/2025). Global hypokinesis with apical akinesis. Progressive LV dilation.
2. MODERATE FUNCTIONAL MITRAL REGURGITATION — secondary to LV dilation and annular stretching. Worsened from mild on prior study.
3. Grade III (restrictive) diastolic dysfunction with elevated filling pressures (E/e' 18). Progressed from Grade II on prior.
4. Pulmonary hypertension — RVSP 48 mmHg (previously 38). Early RV dysfunction (TAPSE 14mm).
5. LA moderately dilated. IVC dilated with reduced collapsibility — elevated RAP.
6. New small pericardial effusion — likely related to heart failure. No tamponade physiology.

CLINICAL CORRELATION:
Interval decline in LVEF from 40% to 35% with progressive chamber dilation, worsening MR, and new pericardial effusion indicates advancing systolic heart failure. Recommend optimization of guideline-directed medical therapy (GDMT): evaluate for ARNI transition, uptitrate beta-blocker, consider hydralazine/nitrate. If continued decline despite optimized GDMT, evaluate candidacy for ICD (primary prevention if LVEF remains ≤35% after 3 months of optimized therapy). Given patient's age, comorbidities (CKD-4, Alzheimer's), and functional status, goals-of-care discussion regarding advanced therapies vs. comfort-focused management is strongly recommended.

Electronically signed: Robert Kim, MD, FACC
Board Certified — Cardiovascular Disease`,
  },
  {
    date: '12/10/2025',
    type: 'Nephrology Consultation',
    category: 'Consult' as const,
    provider: 'Dr. James Park, MD — Nephrology',
    excerpt: `NEPHROLOGY CONSULTATION NOTE

Patient: Williams, Dorothy A.     MRN: 00715
Date of Service: 12/10/2025
Referring Provider: Dr. Michael Torres, MD
Reason for Consultation: CKD Stage 4 management. Declining eGFR trend. Pre-ESRD planning discussion.

SUBJECTIVE:
Mrs. Williams is an 80-year-old female followed for CKD Stage 4. She is accompanied by her daughter, who manages her medications and appointments. Patient reports she feels "tired all the time" but attributes this to "being old." She denies nausea, vomiting, or metallic taste. Appetite is fair — eating about 75% of meals. She reports nocturia x2–3 per night, unchanged. No hematuria or dysuria. Mild bilateral lower extremity swelling, stable. No pruritus, restless legs, or muscle cramps.

CKD trajectory: eGFR trending 34 → 28 → 24 → 22 over 2 years. No episodes of AKI since hospital discharge in August.

OBJECTIVE:
Vitals: BP 136/80 mmHg (goal <130/80), HR 72 bpm, Weight 137 lbs.
General: Thin, frail-appearing elderly female. Oriented to person, partially to place.
Cardiovascular: RRR. S3 present. Mild JVD.
Abdomen: Soft. No CVA tenderness. No palpable kidneys.
Extremities: 1+ bilateral ankle edema. Skin thin, dry. No uremic frost.

Labs reviewed (12/01/2025):
- Creatinine: 3.1 mg/dL (stable from discharge baseline 3.2), BUN 36
- eGFR (CKD-EPI): 24 mL/min — CKD Stage 4
- Potassium: 4.4 mEq/L
- CO2: 20 mEq/L (low — metabolic acidosis)
- Phosphorus: 5.1 mg/dL (H)
- Intact PTH: 185 pg/mL (H — secondary hyperparathyroidism)
- 25-OH Vitamin D: 16 ng/mL (deficient)
- Calcium: 8.6 mg/dL (low-normal)
- Albumin: 3.5 g/dL (low-normal)
- Hemoglobin: 10.9 g/dL (anemia of CKD)
- Iron: 48 mcg/dL (L), Ferritin 22 ng/mL (L), TSAT 14% (L) — iron deficiency

Renal Ultrasound (10/2025): Bilateral small kidneys (R: 9.2 cm, L: 9.0 cm — decreased from 10.5/10.2 cm in 2023). Increased echogenicity bilaterally. Cortical thinning. No hydronephrosis.

ASSESSMENT:
1. Chronic kidney disease, Stage 4 (N18.4) — eGFR 24 mL/min, declining at approximately 3–4 mL/min/year. Etiology: hypertensive nephrosclerosis, cardiorenal syndrome. Kidneys bilateral small with cortical thinning — consistent with chronic parenchymal disease. At current trajectory, anticipated eGFR <15 (Stage 5/ESRD) within 2–3 years.
2. CKD-mineral bone disease — Secondary hyperparathyroidism (PTH 185), hyperphosphatemia (5.1), vitamin D deficiency (16). Requires treatment to prevent renal osteodystrophy, especially given concurrent osteoporosis and fall risk.
3. Metabolic acidosis of CKD — CO2 20 mEq/L. Contributes to muscle wasting, bone loss, and CKD progression. Requires sodium bicarbonate supplementation.
4. Anemia of CKD with concurrent iron deficiency — Hgb 10.8, iron-deficient pattern (ferritin 22, TSAT 14%). Mixed etiology: EPO deficiency from CKD + poor iron intake (decreased appetite) + possible GI losses (on aspirin equivalent?). Iron repletion needed before considering ESA.
5. Chronic systolic heart failure — Cardiorenal syndrome contributing to renal decline. Volume management critical.

PLAN:
1. Phosphorus management: Start sevelamer 800mg with meals TID (phosphate binder). Dietary phosphorus restriction — avoid processed foods, cola, dairy excess. Target phosphorus <4.5.
2. Secondary hyperparathyroidism: Start calcitriol 0.25 mcg daily (active vitamin D analog). Monitor calcium — hold if Ca >10.2. Continue cholecalciferol 2000 IU daily for nutritional D.
3. Metabolic acidosis: Start sodium bicarbonate 650mg (8 mEq) PO BID. Target CO2 ≥22 mEq/L. Evidence supports slowing CKD progression with bicarbonate supplementation.
4. Iron deficiency: Start ferrous sulfate 325mg PO daily (65mg elemental iron), taken on empty stomach with vitamin C for absorption. If oral iron not tolerated or ineffective in 6 weeks, will arrange IV iron infusion (iron sucrose — preferred in CKD). Target ferritin >100, TSAT >20%.
5. Anemia: Once iron-replete, if Hgb remains <10 g/dL, initiate epoetin alfa (EPO) — target Hgb 10–11.5 g/dL per KDIGO. Do not exceed 11.5.
6. Pre-ESRD planning: Discussed with patient and daughter. Given patient's age (80), advanced Alzheimer's, functional decline, and multiple comorbidities, dialysis may not align with goals of care. Daughter expresses concern about quality of life on dialysis. Recommended palliative nephrology approach with conservative kidney management (CKM) as an option. Will revisit as eGFR approaches 15. No vascular access placed at this time.
7. Continue current heart failure regimen. Coordinate with Dr. Torres on volume status management — avoid aggressive diuresis that worsens renal function.
8. Labs in 3 months: BMP, CBC, iron studies, phosphorus, PTH, calcium, albumin.
9. Return to nephrology in 3 months. Call for acute symptoms: nausea/vomiting, confusion beyond baseline, oliguria, significant edema worsening.

Electronically signed: James Park, MD, FASN
Board Certified — Nephrology, Internal Medicine`,
  },
  {
    date: '08/14/2025',
    type: 'Discharge Summary — CHF Exacerbation',
    category: 'Discharge' as const,
    provider: 'Dr. Rachel Adams, MD — Hospital Medicine',
    excerpt: `DISCHARGE SUMMARY

Patient: Williams, Dorothy A.     MRN: 00715
Admission Date: 08/08/2025        Discharge Date: 08/14/2025
Length of Stay: 6 days
Admitting Diagnosis: Acute on chronic systolic heart failure exacerbation
Discharge Disposition: Home with home health services
Attending Physician: Dr. Rachel Adams, MD

ADMISSION HISTORY:
80-year-old female with known chronic systolic CHF (LVEF 45% on echo 03/2025), CKD Stage 4, hypertension, Alzheimer's disease, and osteoporosis, brought to ED by daughter after 3 days of progressive dyspnea, orthopnea (sleeping in recliner), bilateral lower extremity swelling, and 8 lb weight gain over 1 week. Patient had been non-adherent to fluid restriction per daughter — drinking significantly more water due to summer heat. Patient confused on arrival, unable to provide reliable history.

ED presentation: BP 162/94, HR 104 irregular (new atrial fibrillation), RR 28, SpO2 88% on RA → 94% on 4L NC. Bilateral pulmonary crackles to mid-lung fields. JVP 14 cm. 3+ bilateral pitting edema to knees. BNP 1,842 pg/mL (baseline ~400). Creatinine 4.1 mg/dL (baseline 3.0). CXR: bilateral pleural effusions, pulmonary vascular congestion, cardiomegaly.

HOSPITAL COURSE:

Day 1–2: Initiated IV furosemide 80mg BID. Strict I&O. Fluid restricted to 1L/day. Transitioned to telemetry. Atrial fibrillation with RVR (rate 110–130) → rate controlled with IV diltiazem drip then transitioned to oral digoxin (adjusted for renal function). Cardiology consulted — recommended against anticoagulation given fall risk and Alzheimer's. CHA2DS2-VASc score 6 (high stroke risk) but HAS-BLED 5 (high bleed risk); shared decision with daughter (healthcare proxy) to defer anticoagulation. Net negative 2.8L Day 1, 2.1L Day 2.

Day 3–4: Significant diuresis. Weight down 12 lbs from admission. Creatinine peaked at 4.6 mg/dL (cardiorenal syndrome) then trended down to 3.8 with slower diuresis. Furosemide reduced to 40mg BID. SpO2 improved to 95% on RA. Able to lie flat. Bilateral crackles resolving. Edema improved to 1+. AF converted to sinus rhythm spontaneously Day 3 — digoxin continued at renal dose.

Day 5–6: Clinically euvolemic. Weight stable at discharge weight (134 lbs, down 12 lbs from admission 146 lbs). Creatinine improved to 3.2 (near baseline). BNP down to 680. Ambulating with walker 100 feet without significant dyspnea. O2 sat 94% on RA at rest. Tolerating oral medications and diet. Daughter trained on daily weight monitoring, medication administration, and when to call.

Echocardiogram (08/12/2025): LVEF 40% (declined from 45% on 03/2025). Moderate LV dilation. Mild mitral regurgitation (functional). Estimated RVSP 38 mmHg.

DISCHARGE DIAGNOSES:
1. Acute on chronic systolic heart failure exacerbation (I50.23) — NYHA Class III-IV on admission, Class II-III at discharge. LVEF declined to 40%. Precipitants: fluid non-adherence, possible paroxysmal AF.
2. Paroxysmal atrial fibrillation (I48.0) — new diagnosis. Spontaneously converted. Rate controlled with digoxin. Anticoagulation deferred per shared decision (high fall risk + dementia).
3. Acute kidney injury on CKD Stage 4 (N17.9 + N18.4) — cardiorenal syndrome Type 1. Creatinine peaked 4.6, resolved to 3.2 with diuresis. Baseline eGFR 24.
4. CKD Stage 4 (N18.4) — baseline Cr 3.0, eGFR 24
5. Alzheimer's disease (G30.1) — complicated medication management and fluid adherence
6. Hypertension (I10) — controlled during admission
7. Osteoporosis (M81.0) — fall precautions maintained

DISCHARGE MEDICATIONS:
1. Furosemide 40mg PO daily (increased from 20mg; net change from admission)
2. Carvedilol 12.5mg PO BID (continued)
3. Lisinopril 5mg PO daily (held during AKI, restarted Day 5 at reduced dose from 10mg)
4. Digoxin 0.0625mg PO daily (NEW — renal-adjusted dose for AF rate control)
5. Atorvastatin 40mg PO QHS (continued)
6. Alendronate 70mg PO weekly (continued)
7. Donepezil 5mg PO QHS (continued)
8. Cyanocobalamin 1000 mcg IM monthly (continued)
9. Potassium Chloride 20 mEq PO daily (NEW — with increased furosemide)

DISCONTINUED: Digoxin loading dose (completed)

FOLLOW-UP:
1. PCP Dr. Michael Torres: 1 week post-discharge (scheduled 08/21/2025)
2. Cardiology Dr. Robert Kim: 4 weeks (repeat echo in 3 months)
3. Nephrology: 2 weeks — recheck creatinine and potassium
4. Home Health: Visiting nurse 3x/week for vitals, weights, medication oversight, and CHF education

DISCHARGE INSTRUCTIONS TO PATIENT/CAREGIVER:
1. Weigh daily, same time, same scale. Call clinic if weight gain >3 lbs in 48 hours or >5 lbs in 1 week.
2. Fluid restriction: 1.5 liters/day (approximately 6 cups). Record intake.
3. Low-sodium diet: <2g sodium daily. Avoid canned soups, processed foods, added salt.
4. Take all medications as directed. Do NOT skip doses of furosemide, carvedilol, or lisinopril.
5. Activity: Walk daily as tolerated. Use walker at all times. Rest when short of breath.
6. Return to ED for: sudden weight gain >3 lbs, worsening shortness of breath, inability to lie flat, chest pain, dizziness/fainting, new leg swelling.

CONDITION AT DISCHARGE: Stable, Improved.

Electronically signed: Rachel Adams, MD
Board Certified — Internal Medicine, Hospital Medicine`,
  },
  {
    date: '11/08/2024',
    type: 'DEXA Scan — Bone Density',
    category: 'Imaging' as const,
    provider: 'Dr. Amanda Torres, MD — Radiology',
    excerpt: `DUAL-ENERGY X-RAY ABSORPTIOMETRY (DEXA) — BONE MINERAL DENSITY

Patient: Williams, Dorothy A.     MRN: 00715
Date of Study: 11/08/2024
Ordering Provider: Dr. Michael Torres, MD
Indication: Osteoporosis monitoring. On alendronate therapy. History of falls.
Comparison: DEXA 11/2022
Manufacturer: Hologic Horizon A

RESULTS:

Lumbar Spine (L1–L4):
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Region      BMD (g/cm²)   T-score     Z-score     WHO Classification
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
L1          0.742         -2.8        -0.6        Osteoporosis
L2          0.768         -2.6        -0.4        Osteoporosis
L3          0.801         -2.3        -0.2        Osteoporosis
L4          0.812         -2.2        -0.1        Osteoporosis
L1-L4       0.781         -2.5        -0.3        Osteoporosis
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Change from 11/2022: L1-L4 BMD -2.1% (statistically significant >LSC)

Left Femoral Neck:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Region          BMD (g/cm²)   T-score    Z-score     WHO Classification
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Femoral Neck    0.558         -3.2       -0.9        Osteoporosis
Total Hip       0.612         -2.9       -0.7        Osteoporosis
Trochanter      0.498         -2.6       -0.5        Osteoporosis
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Change from 11/2022: Femoral Neck BMD -3.4% (significant decline)

FRAX® 10-Year Fracture Risk (without BMD):
- Major osteoporotic fracture: 28%
- Hip fracture: 14%

FRAX® 10-Year Fracture Risk (with BMD):
- Major osteoporotic fracture: 34%
- Hip fracture: 19%

IMPRESSION:
1. OSTEOPOROSIS at all measured sites — T-scores ranging from -2.2 to -3.2. Most severe at left femoral neck (T-score -3.2).
2. SIGNIFICANT BONE LOSS since prior study (11/2022): Lumbar spine -2.1%, femoral neck -3.4%. This decline is greater than the least significant change (LSC), indicating true bone loss despite alendronate therapy.
3. HIGH FRACTURE RISK — FRAX 10-year hip fracture risk 19%. Combined with history of recurrent falls, Alzheimer's disease, low BMI (20.6), and declining muscle mass, this patient is at very high fracture risk.

RECOMMENDATIONS:
1. Current alendronate therapy appears suboptimal — consider alternative agent. For high-risk patients with inadequate response to oral bisphosphonates, options include: IV zoledronic acid (5mg annually), denosumab (60mg SQ every 6 months), or anabolic agents (teriparatide/abaloparatide — limited by CKD Stage 4, contraindicated if eGFR <30).
2. Ensure adequate calcium (1200mg/day total from diet + supplement) and vitamin D (target 25-OH >30 ng/mL).
3. Fall prevention is critical given fracture risk — physical therapy, home safety evaluation, medication review.
4. Repeat DEXA in 2 years, or sooner if therapy changed.

Electronically signed: Amanda Torres, MD
Board Certified — Diagnostic Radiology`,
  },
  {
    date: '04/10/2023',
    type: 'Neurology — Memory Clinic Evaluation',
    category: 'Consult' as const,
    provider: 'Dr. Maria Santos, MD — Neurology / Cognitive Disorders',
    excerpt: `MEMORY CLINIC — COMPREHENSIVE COGNITIVE EVALUATION

Patient: Williams, Dorothy A.     MRN: 00715
Date of Evaluation: 04/10/2023
Referring Provider: Dr. Michael Torres, MD
Reason for Referral: Progressive memory loss over 12–18 months with functional decline.

HISTORY (obtained from patient and daughter):
Mrs. Williams is a 78-year-old right-handed female referred for evaluation of progressive cognitive decline. Daughter reports the following concerns developing over the past 12–18 months:

Memory: Repeating questions multiple times in the same conversation. Forgetting recent phone conversations. Misplacing items (keys, purse) daily. Left the stove on twice. Forgot granddaughter's birthday for the first time.
Language: Occasional word-finding difficulty. Uses vague terms ("the thing") more often. Comprehension intact for simple instructions but struggles with complex multi-step directions.
Visuospatial: Got lost driving to her church (familiar route for 30+ years) — stopped driving after this incident 6 months ago.
Executive function: Difficulty managing medications — found expired food in refrigerator. Unable to balance checkbook (previously managed independently). Trouble following recipes she's made for decades.
Behavior/personality: More withdrawn. Less interested in social activities. Occasional irritability. No hallucinations, delusions, or paranoia. No wandering.
ADLs: Independent with basic ADLs (feeding, toileting, basic hygiene). Requires supervision for bathing (forgets steps). Needs help with meal preparation, medication management, finances. Daughter manages laundry and housekeeping.

Past cognitive history: No prior formal cognitive evaluation. Patient has always been "sharp" per daughter. Completed high school, worked as a bookkeeper for 35 years (retired age 65). Socially active until recently.

Family history: Mother had "senility" in her 80s (died age 87). No known genetic testing in family.

EXAMINATION:

Neurological Exam:
- Cranial nerves: II–XII intact. Pupils equal, reactive. Visual fields full. No facial asymmetry.
- Motor: 5/5 strength throughout. No rigidity (rules against Lewy body or Parkinsonian features). Normal tone.
- Sensory: Intact to light touch, pinprick, and vibration.
- Reflexes: 2+ symmetric. No Babinski. No frontal release signs (grasp, palmomental, snout all negative).
- Coordination: Finger-to-nose intact. No tremor.
- Gait: Mildly cautious but normal base, normal stride length. No shuffling (argues against DLB/PD). Romberg negative.

Cognitive Testing:

Mini-Mental State Examination (MMSE): 24/30
- Orientation: 8/10 (missed day of week and county)
- Registration: 3/3
- Attention/Calculation: 3/5 (serial 7s)
- Recall: 1/3 (significant delayed recall deficit)
- Language: 8/8
- Visuospatial: 1/1

Montreal Cognitive Assessment (MoCA): 19/30
- Visuospatial/Executive: 2/5 (trails error, cube copy poor, clock 2/3)
- Naming: 3/3
- Attention: 4/6
- Language: 2/3 (sentence repetition error)
- Abstraction: 1/2
- Delayed Recall: 1/5 (severe impairment — only recalled 1/5 words with no benefit from category cue)
- Orientation: 5/6 (missed date)

Clock Drawing Test: Drew circle appropriately. Numbers placed correctly. When asked to set 11:10, placed hands at 11 and 10 — stimulus-bound error (pointing to 10 and 11 rather than interpreting the time). Classic executive/conceptual error pattern.

Verbal Fluency:
- Category (animals): 9 in 60 seconds (impaired; expected ≥15 for age/education)
- Letter (F-A-S): 24 total (low-normal)

Geriatric Depression Scale (GDS-15): 6/15 (mild depressive symptoms — below threshold for major depression at this time, but monitor)

DIAGNOSTIC WORKUP:

Labs (04/2023):
- TSH: 2.8 mIU/L (normal — rules out hypothyroid cognitive impairment)
- B12: 310 pg/mL (normal)
- Folate: 14.2 ng/mL (normal)
- RPR: Non-reactive (rules out neurosyphilis)
- CBC, BMP: At baseline for her conditions
- Vitamin D: 22 ng/mL (insufficient but not causative)

MRI Brain with and without contrast (04/2023):
- Bilateral hippocampal atrophy, moderate severity (Scheltens Grade 3 right, Grade 2 left — disproportionate to overall cortical atrophy). Medial temporal lobe volume loss.
- Bilateral temporoparietal cortical atrophy.
- No acute infarct, mass, or hemorrhage.
- Moderate periventricular white matter T2/FLAIR hyperintensities — attributed to chronic microvascular ischemic disease and hypertension.
- No evidence of normal pressure hydrocephalus (ventricles proportionate to sulcal enlargement).

ASSESSMENT:
1. PROBABLE ALZHEIMER'S DISEASE — Late onset (G30.1), with dementia (F02.80, without behavioral disturbance)
   Clinical diagnosis based on NIA-AA 2023 criteria:
   a. Insidious onset, progressive course over 12–18 months
   b. Amnestic presentation — prominent episodic memory deficit (recall 1/3 MMSE, 1/5 MoCA)
   c. Additional cognitive domains impaired: executive function (clock drawing, trails), visuospatial (cube copy, got lost)
   d. Functional decline: IADL impairment (medications, finances, cooking, driving)
   e. Supportive neuroimaging: disproportionate hippocampal/medial temporal lobe atrophy on MRI
   f. Reversible causes excluded (TSH, B12, RPR, structural lesion)
   g. No features suggesting alternative etiology (no parkinsonism, no hallucinations, no stepwise decline, no frontal release signs)

   Stage: Mild-to-Moderate dementia (MMSE 24, MoCA 19, IADL impairments with preserved basic ADLs)

2. Mild depressive symptoms — GDS 6/15. Subsyndromal but monitor for worsening as cognition declines. Depression is common comorbidity in early-moderate AD and may accelerate functional decline.

PLAN:
1. Start donepezil (Aricept) 5mg PO at bedtime. Cholinesterase inhibitor for symptomatic treatment. Titrate to 10mg after 4–6 weeks if tolerated. Side effects to monitor: GI upset, vivid dreams, bradycardia (relevant given cardiac history).
2. Vitamin D supplementation: Cholecalciferol 2000 IU daily.
3. Defer memantine at this time — typically added at moderate-severe stage or if donepezil alone insufficient.
4. Cognitive engagement: Encourage social activities, puzzles, reading. Cognitive stimulation therapy if available.
5. Safety: Remove stove knobs or install auto-shutoff. No driving (already stopped). Ensure Medic-Alert ID. Door alarms if wandering develops. Discuss firearm removal if applicable (daughter confirms none in home).
6. Caregiver support: Provided Alzheimer's Association local chapter contact. Discussed disease trajectory. Daughter amenable to caregiver support group.
7. Legal/financial planning: Strongly recommend completing advance directives, healthcare proxy designation, and financial power of attorney while patient retains capacity for these decisions. Patient currently has capacity to participate in this planning.
8. Genetic testing: Not recommended routinely for late-onset AD. Would consider only if family requests.
9. Follow-up: Neurology/Memory Clinic in 6 months. Repeat cognitive testing annually. Sooner for behavioral changes, rapid decline, or medication concerns.

Electronically signed: Maria Santos, MD
Board Certified — Neurology, Behavioral Neurology & Neuropsychiatry`,
  },
];
